import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  CoupleSettings,
  MemoryItem,
  DailyLetter,
  SpecialEvent,
  MediaType,
  TimeElapsed,
  TimeOfDay,
  WeatherType,
  AuthorRole,
  VisitorLog,
  NavVisibilitySettings,
} from '../types';
import {
  LayoutDashboard,
  Lock,
  Unlock,
  KeyRound,
  Eye,
  EyeOff,
  ShieldCheck,
  ShieldAlert,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Image as ImageIcon,
  Flower2,
  Heart,
  Shield,
  Database,
  Plus,
  Trash2,
  Edit3,
  Calendar,
  Clock,
  MapPin,
  Save,
  Download,
  Upload,
  CheckCircle2,
  LogOut,
  ExternalLink,
  MessageSquare,
  Search,
  Filter,
  Check,
  X,
  AlertTriangle,
  AlertCircle,
  Layers,
  ChevronRight,
  Globe,
  Sliders,
  Maximize2,
  RefreshCw,
  Gift,
  Cake,
  Flower,
  Flame,
  Copy,
  Music,
  Share2,
  Sun,
  Moon,
  Sunrise,
  Sunset,
  Cloud,
  CloudRain,
  Mail,
  User,
  Users,
  UserCheck,
  Compass,
  Camera,
  Droplets,
  Quote,
  Smartphone,
  Wrench,
} from 'lucide-react';
import { romanticAudio } from '../utils/audio';
import { formatDateSpanish, formatDateTimeSpanish, calculateTimeElapsed } from '../utils/storage';
import { compressImageFile } from '../utils/imageCompressor';
import { detectBestDateFromPhotoBatch, formatReadableSpanishDate } from '../utils/photoDateExtractor';
import { isLetterSealed } from '../utils/letterLockUtils';
import { DailyLetterModal } from './DailyLetterModal';
import { ConfirmationModal } from './ConfirmationModal';
import { LetterAuditModal } from './LetterAuditModal';
import { VisitorRegistrySection } from './admin/VisitorRegistrySection';
import { MusicAdminSection } from './admin/MusicAdminSection';
import { MyWebAdminSection } from './admin/MyWebAdminSection';
import { TulipAdminSection } from './admin/TulipAdminSection';
import { WateringAdminSection } from './admin/WateringAdminSection';
import { PhrasesAdminSection } from './admin/PhrasesAdminSection';
import { AppAdminSection } from './admin/AppAdminSection';
import { WebSettingsAdminSection } from './admin/WebSettingsAdminSection';
import { ModernAdminLogin } from './admin/ModernAdminLogin';
import { ModernAdminNav } from './admin/ModernAdminNav';
import { ModernAdminDashboard } from './admin/ModernAdminDashboard';
import { saveProfileAvatarToDb, registerAdminRecordInDb, updateWebMaintenanceStatusInDb } from '../services/firebaseDb';
import { MyWebRequest, WebRequestStatus } from '../types';
import { calculateHydrationState } from '../utils/wateringUtils';
import { updatePassword } from 'firebase/auth';
import { auth } from '../firebase';
import {
  createPartnerAuthAccount,
  syncOrRegisterPartnerAccount,
  requestPasswordResetCode,
  updateCurrentUserPassword,
  configurePartnerEmails,
} from '../services/authService';

interface AdminPageProps {
  settings: CoupleSettings;
  memories: MemoryItem[];
  dailyLetters?: DailyLetter[];
  specialEvents?: DailyLetter[];
  visitorLogs?: VisitorLog[];
  webRequests?: MyWebRequest[];
  isAuthenticated: boolean;
  activeAdminProfile?: 'novio' | 'novia';
  onLogin: (profile?: 'novio' | 'novia') => void;
  onLogout: () => void;
  onNavigateToPublic: (route?: string) => void;
  onSaveSettings: (newSettings: CoupleSettings) => void;
  onUpdateMemories: (newMemories: MemoryItem[]) => void;
  onUpdateDailyLetters?: (newLetters: DailyLetter[]) => void;
  onUpdateSpecialEvents?: (newEvents: DailyLetter[]) => void;
  onDeleteMemory?: (id: string) => void;
  onDeleteDailyLetter?: (id: string) => void;
  onDeleteVisitorLog?: (id: string) => Promise<void> | void;
  onClearAllVisitorLogs?: () => Promise<void> | void;
  onBlockIp?: (
    ip: string,
    meta?: {
      reason?: string;
      deviceModel?: string;
      visitorId?: string;
      country?: string;
      city?: string;
      flagEmoji?: string;
    }
  ) => Promise<void> | void;
  onUnblockIp?: (ip: string, visitorId?: string) => Promise<void> | void;
  onUpdateWebRequestStatus?: (
    id: string,
    status: WebRequestStatus,
    adminNotes?: string,
    extraAdminFields?: Partial<MyWebRequest>
  ) => Promise<void> | void;
  onUpdateWebRequest?: (updatedRequest: MyWebRequest) => Promise<void> | void;
  onDeleteWebRequest?: (id: string) => Promise<void> | void;
  currentSection?: AdminSection;
  onSectionChange?: (section: AdminSection) => void;
  onOpenUploadModal?: () => void;
  onImportData?: (imported: {
    settings?: CoupleSettings;
    memories?: MemoryItem[];
    dailyLetters?: DailyLetter[];
    specialEvents?: DailyLetter[];
  }) => void;
  onResetDefaultData?: () => void;
  isUserWeb?: boolean;
  userWebOrderCode?: string;
}

export type AdminSection =
  | 'dashboard'
  | 'visitors'
  | 'memories'
  | 'letters'
  | 'atmosphere'
  | 'tulips'
  | 'watering'
  | 'phrases'
  | 'couple'
  | 'security'
  | 'database'
  | 'navigation'
  | 'music'
  | 'myweb'
  | 'app'
  | 'web_settings';

export const ADMIN_SECTION_PATHS: Record<AdminSection, string> = {
  dashboard: '/admin',
  visitors: '/admin/estadisticas',
  memories: '/admin/recuerdos',
  letters: '/admin/cartas',
  atmosphere: '/admin/ambiente',
  tulips: '/admin/tulipanes',
  watering: '/admin/riego',
  phrases: '/admin/frases',
  couple: '/admin/pareja',
  security: '/admin/seguridad',
  database: '/admin/basededatos',
  navigation: '/admin/navegacion',
  music: '/admin/musica',
  myweb: '/admin/mi-web',
  app: '/admin/app',
  web_settings: '/admin/ajustes-web',
};

export const ADMIN_SECTION_TITLES: Record<AdminSection, string> = {
  dashboard: 'Panel Principal',
  visitors: 'Estadísticas de la web',
  memories: 'Álbum & Recuerdos',
  letters: 'Cartas',
  atmosphere: 'Ambiente & Cielo',
  tulips: 'Color de Tulipanes',
  watering: 'Riego & Jardín',
  phrases: 'Frases & Dedicatorias',
  couple: 'Pareja & Perfiles',
  security: 'Seguridad & Clave',
  database: 'Base de Datos & Nube',
  navigation: 'Interfaces & Navegación',
  music: 'Música & Playlist',
  myweb: 'Solicitudes Mi Web',
  app: 'App Móvil & Descargas',
  web_settings: 'Ajustes Web',
};

export function getAdminSectionFromPath(pathname: string): AdminSection {
  const clean = pathname.toLowerCase().replace(/\/+$/, '').trim();
  if (clean === '/admin/ajustes-web' || clean === '/admin/ajustes' || clean === '/admin/web' || clean === '/admin/mantenimiento' || clean === '/admin/web-settings') {
    return 'web_settings';
  }
  if (clean === '/admin/app' || clean === '/admin/aplicacion' || clean === '/admin/descargar-app' || clean === '/admin/descarga') {
    return 'app';
  }
  if (clean === '/admin/estadisticas' || clean === '/admin/visitas' || clean === '/admin/visitante' || clean === '/admin/visitantes' || clean === '/admin/visitors') {
    return 'visitors';
  }
  if (clean === '/admin/recuerdos' || clean === '/admin/memories' || clean === '/admin/album' || clean === '/admin/fotos') {
    return 'memories';
  }
  if (clean === '/admin/cartas' || clean === '/admin/letters') {
    return 'letters';
  }
  if (clean === '/admin/eventos' || clean === '/admin/events') {
    return 'dashboard';
  }
  if (clean === '/admin/ambiente' || clean === '/admin/atmosphere' || clean === '/admin/cielo' || clean === '/admin/jardin') {
    return 'atmosphere';
  }
  if (clean === '/admin/riego' || clean === '/admin/regadera' || clean === '/admin/watering' || clean === '/admin/flores-riego') {
    return 'watering';
  }
  if (clean === '/admin/frases' || clean === '/admin/quotes' || clean === '/admin/dedicatorias') {
    return 'phrases';
  }
  if (clean === '/admin/tulipanes' || clean === '/admin/tulips' || clean === '/admin/flores') {
    return 'tulips';
  }
  if (clean === '/admin/pareja' || clean === '/admin/couple' || clean === '/admin/perfil') {
    return 'couple';
  }
  if (clean === '/admin/seguridad' || clean === '/admin/security' || clean === '/admin/claves' || clean === '/admin/pin') {
    return 'security';
  }
  if (clean === '/admin/basededatos' || clean === '/admin/database' || clean === '/admin/datos' || clean === '/admin/nube') {
    return 'database';
  }
  if (clean === '/admin/navegacion' || clean === '/admin/navigation' || clean === '/admin/interfaces' || clean === '/admin/menu') {
    return 'navigation';
  }
  if (clean === '/admin/musica' || clean === '/admin/music' || clean === '/admin/canciones' || clean === '/admin/playlist') {
    return 'music';
  }
  if (clean === '/admin/mi-web' || clean === '/admin/miweb' || clean === '/admin/solicitudes' || clean === '/admin/web-requests') {
    return 'myweb';
  }
  return 'dashboard';
}

export const AdminPage: React.FC<AdminPageProps> = ({
  settings,
  memories,
  dailyLetters = [],
  specialEvents = [],
  visitorLogs = [],
  webRequests = [],
  isAuthenticated,
  activeAdminProfile,
  onLogin,
  onLogout,
  onNavigateToPublic,
  onSaveSettings,
  onUpdateMemories,
  onUpdateDailyLetters,
  onUpdateSpecialEvents,
  onDeleteMemory,
  onDeleteDailyLetter,
  onDeleteVisitorLog,
  onClearAllVisitorLogs,
  onBlockIp,
  onUnblockIp,
  onUpdateWebRequestStatus,
  onUpdateWebRequest,
  onDeleteWebRequest,
  currentSection,
  onSectionChange,
  onOpenUploadModal,
  onImportData,
  onResetDefaultData,
  isUserWeb = false,
  userWebOrderCode,
}) => {
  // Combine letters input
  const lettersList: DailyLetter[] = dailyLetters.length > 0 ? dailyLetters : specialEvents;
  const handleLettersUpdate = (updated: DailyLetter[]) => {
    if (onUpdateDailyLetters) {
      onUpdateDailyLetters(updated);
    } else if (onUpdateSpecialEvents) {
      onUpdateSpecialEvents(updated);
    }
  };



  const currentEffectiveProfile = activeAdminProfile || 'novio';

  // Active admin section synchronized with URL route
  const [activeSection, setActiveSection] = useState<AdminSection>(() => {
    if (currentSection) return currentSection;
    if (typeof window !== 'undefined') {
      return getAdminSectionFromPath(window.location.pathname);
    }
    return 'dashboard';
  });

  // Keep in sync with parent component currentSection
  React.useEffect(() => {
    if (currentSection && currentSection !== activeSection) {
      setActiveSection(currentSection);
    }
  }, [currentSection, activeSection]);

  // Synchronize on browser history navigation (popstate)
  React.useEffect(() => {
    const handlePop = () => {
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
        const sec = getAdminSectionFromPath(window.location.pathname);
        setActiveSection(sec);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const handleSelectSection = (section: AdminSection) => {
    setActiveSection(section);
    const targetPath = ADMIN_SECTION_PATHS[section] || '/admin';
    if (typeof window !== 'undefined' && window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
    if (onSectionChange) {
      onSectionChange(section);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const copyRouteLink = (path: string) => {
    if (typeof window !== 'undefined' && navigator.clipboard) {
      const fullUrl = `${window.location.origin}${path}`;
      navigator.clipboard.writeText(fullUrl);
      triggerToast(`Enlace copiado: ${path}`);
    }
  };

  const renderSectionBreadcrumbHeader = (
    title: string,
    subtitle: string,
    _section: AdminSection,
    actionButton?: React.ReactNode
  ) => {
    return (
      <div className="mb-6">
        {/* Main Section Header Card */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 tracking-tight">
              {title}
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          </div>

          {actionButton && <div className="shrink-0">{actionButton}</div>}
        </div>
      </div>
    );
  };

  const renderSectionBottomReturnBar = () => {
    return (
      <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs">
        <div className="flex items-center gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold shrink-0 border border-rose-100 shadow-2xs">
            <LayoutDashboard className="w-4 h-4" />
          </div>
          <div className="text-xs text-slate-600">
            <p className="font-bold text-slate-900 text-sm">¿Deseas gestionar otra opción del panel?</p>
            <p className="text-slate-500 text-xs mt-0.5">
              Cada sección es independiente. Regresa al menú principal para acceder a los otros módulos.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            handleSelectSection('dashboard');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs transition-all shadow-xs active:scale-95 cursor-pointer w-full sm:w-auto shrink-0"
        >
          <ArrowLeft className="w-4 h-4 text-rose-400" />
          <span>Volver al Menú Principal</span>
        </button>
      </div>
    );
  };

  const getNextEventCountdown = (month: number, day: number) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    let target = new Date(currentYear, month - 1, day, 0, 0, 0);
    if (target.getTime() < now.getTime() - 24 * 60 * 60 * 1000) {
      target = new Date(currentYear + 1, month - 1, day, 0, 0, 0);
    }
    const diffMs = target.getTime() - now.getTime();
    const daysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    return {
      targetDate: target,
      daysRemaining,
      isToday: daysRemaining === 0,
      formattedDate: target.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' }),
    };
  };

  // Memory search & filters
  const [searchMemory, setSearchMemory] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterAuthor, setFilterAuthor] = useState<'all' | 'novio' | 'novia'>('all');
  const [editingMemory, setEditingMemory] = useState<MemoryItem | null>(null);

  // Daily Letters Management State
  const [searchLetter, setSearchLetter] = useState('');
  const [filterLetterCategory, setFilterLetterCategory] = useState<string>('all');
  const [previewLetter, setPreviewLetter] = useState<DailyLetter | null>(null);
  const [selectedAuditLetter, setSelectedAuditLetter] = useState<DailyLetter | null>(null);

  // Couple settings form state
  const [p1Name, setP1Name] = useState(settings.partner1Name);
  const [p2Name, setP2Name] = useState(settings.partner2Name);
  const [p1BirthDate, setP1BirthDate] = useState(settings.partner1BirthDate || '');
  const [p2BirthDate, setP2BirthDate] = useState(settings.partner2BirthDate || '');
  const [p1BloodType, setP1BloodType] = useState(settings.partner1BloodType || 'O+');
  const [p2BloodType, setP2BloodType] = useState(settings.partner2BloodType || 'O+');
  const [p1Title, setP1Title] = useState(settings.partner1Title || 'El Novio');
  const [p2Title, setP2Title] = useState(settings.partner2Title || 'La Novia');
  const [p1Avatar, setP1Avatar] = useState(() => {
    if (isUserWeb) return settings.partner1Avatar || '';
    let cached = '';
    try {
      cached = typeof window !== 'undefined' ? localStorage.getItem('couple_avatar_novio') || '' : '';
    } catch {}
    return settings.partner1Avatar || cached;
  });
  const [p2Avatar, setP2Avatar] = useState(() => {
    if (isUserWeb) return settings.partner2Avatar || '';
    let cached = '';
    try {
      cached = typeof window !== 'undefined' ? localStorage.getItem('couple_avatar_novia') || '' : '';
    } catch {}
    return settings.partner2Avatar || cached;
  });
  const [isSavingAvatar1, setIsSavingAvatar1] = useState(false);
  const [isSavingAvatar2, setIsSavingAvatar2] = useState(false);
  const [imgErrorP1, setImgErrorP1] = useState(false);
  const [imgErrorP2, setImgErrorP2] = useState(false);
  const [showUrlInput1, setShowUrlInput1] = useState(false);
  const [urlInput1, setUrlInput1] = useState('');
  const [showUrlInput2, setShowUrlInput2] = useState(false);
  const [urlInput2, setUrlInput2] = useState('');
  const [annivDate, setAnnivDate] = useState(settings.startDate ? settings.startDate.split('T')[0] : '');
  const [romanticQuote, setRomanticQuote] = useState(settings.quote);

  // Active Admin Profile is strictly derived from the authenticated Firebase session
  const activeProfile: 'novio' | 'novia' = activeAdminProfile || 'novio';
  const selectedLoginProfile = activeProfile;

  React.useEffect(() => {
    setP1Name(settings.partner1Name);
    setP2Name(settings.partner2Name);
    setP1Title(settings.partner1Title || 'El Novio');
    setP2Title(settings.partner2Title || 'La Novia');
    if (settings.partner1BirthDate !== undefined) setP1BirthDate(settings.partner1BirthDate);
    if (settings.partner2BirthDate !== undefined) setP2BirthDate(settings.partner2BirthDate);
    if (settings.partner1BloodType) setP1BloodType(settings.partner1BloodType);
    if (settings.partner2BloodType) setP2BloodType(settings.partner2BloodType);
    let cachedNovio = '';
    let cachedNovia = '';
    try {
      if (typeof window !== 'undefined') {
        cachedNovio = localStorage.getItem('couple_avatar_novio') || '';
        cachedNovia = localStorage.getItem('couple_avatar_novia') || '';
      }
    } catch {}
    setP1Avatar(
      isUserWeb
        ? settings.partner1Avatar || ''
        : settings.partner1Avatar || cachedNovio
    );
    setImgErrorP1(false);
    setP2Avatar(
      isUserWeb
        ? settings.partner2Avatar || ''
        : settings.partner2Avatar || cachedNovia
    );
    setImgErrorP2(false);
    setAnnivDate(settings.startDate ? settings.startDate.split('T')[0] : '');
    setRomanticQuote(settings.quote);
  }, [settings, isUserWeb]);

  // Prevent accessing platform and security sections in custom web admin panel
  React.useEffect(() => {
    if (
      isUserWeb &&
      ['visitors', 'security', 'database', 'navigation', 'app', 'web_settings', 'myweb'].includes(activeSection)
    ) {
      setActiveSection('dashboard');
    }
  }, [isUserWeb, activeSection]);

  // Atmosphere Form State
  const [atmosphereMode, setAtmosphereMode] = useState<NonNullable<CoupleSettings['atmosphereMode']>>(
    settings.atmosphereMode || 'auto'
  );
  const [manualTimeOfDay, setManualTimeOfDay] = useState<TimeOfDay>(
    settings.manualTimeOfDay || 'day'
  );
  const [manualWeather, setManualWeather] = useState<WeatherType>(
    settings.manualWeather || 'sunny'
  );
  const [manualMinuteOfDay, setManualMinuteOfDay] = useState<number>(
    settings.manualMinuteOfDay !== undefined ? settings.manualMinuteOfDay : 12 * 60
  );

  // Security Form: Dual-Account Credentials (Novio & Novia)
  const [p1EmailInput, setP1EmailInput] = useState(
    settings.partner1Email || ''
  );
  const [p1NewPass, setP1NewPass] = useState('');
  const [p1ConfirmPass, setP1ConfirmPass] = useState('');
  const [showP1Pass, setShowP1Pass] = useState(false);
  const [p1Loading, setP1Loading] = useState(false);
  const [p1Success, setP1Success] = useState('');
  const [p1Error, setP1Error] = useState('');

  const [p2EmailInput, setP2EmailInput] = useState(
    settings.partner2Email || ''
  );
  const [p2NewPass, setP2NewPass] = useState('');
  const [p2ConfirmPass, setP2ConfirmPass] = useState('');
  const [showP2Pass, setShowP2Pass] = useState(false);
  const [p2Loading, setP2Loading] = useState(false);
  const [p2Success, setP2Success] = useState('');
  const [p2Error, setP2Error] = useState('');

  // Apartado: Crear Cuentas Administrativas (Novio o Novia con sólo correo y contraseña)
  const [createRole, setCreateRole] = useState<'novio' | 'novia'>('novia');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createConfirmPassword, setCreateConfirmPassword] = useState('');
  const [showCreatePass, setShowCreatePass] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState('');
  const [createError, setCreateError] = useState('');

  // States for account configuration toggle and synchronization/repair modal
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [repairModal, setRepairModal] = useState<{ isOpen: boolean; role: 'novio' | 'novia'; email: string } | null>(null);
  const [repairEmailInput, setRepairEmailInput] = useState('');
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairSuccess, setRepairSuccess] = useState('');
  const [repairError, setRepairError] = useState('');

  // Local state for immediate and resilient reflection of account associations
  const [localNoviaEmail, setLocalNoviaEmail] = useState<string>(settings.partner2Email || '');
  const [localNoviaUid, setLocalNoviaUid] = useState<string>(settings.partner2Uid || '');
  const [localNovioEmail, setLocalNovioEmail] = useState<string>(settings.partner1Email || '');
  const [localNovioUid, setLocalNovioUid] = useState<string>(settings.partner1Uid || '');

  React.useEffect(() => {
    if (settings.partner1Email) {
      setP1EmailInput(settings.partner1Email);
      setLocalNovioEmail(settings.partner1Email);
    }
    if (settings.partner1Uid) setLocalNovioUid(settings.partner1Uid);
    if (settings.partner2Email) {
      setP2EmailInput(settings.partner2Email);
      setLocalNoviaEmail(settings.partner2Email);
    }
    if (settings.partner2Uid) setLocalNoviaUid(settings.partner2Uid);
  }, [settings.partner1Email, settings.partner1Uid, settings.partner2Email, settings.partner2Uid]);

  const effectiveNoviaEmail = settings.partner2Email || localNoviaEmail;
  const effectiveNoviaUid = settings.partner2Uid || localNoviaUid;
  const effectiveNovioEmail = settings.partner1Email || localNovioEmail;
  const effectiveNovioUid = settings.partner1Uid || localNovioUid;

  // Navigation Visibility Form State
  const [navVisibility, setNavVisibility] = useState<NavVisibilitySettings>(() => {
    const isMyWebActive =
      settings.navVisibility?.myWeb !== false &&
      (settings.navVisibility as any)?.myweb !== false;
    return {
      garden: settings.navVisibility?.garden !== false,
      blog: settings.navVisibility?.blog !== false,
      letters: settings.navVisibility?.letters !== false,
      animations: settings.navVisibility?.animations !== false,
      myWeb: isMyWebActive,
      myweb: isMyWebActive,
    };
  });
  const [navFeedback, setNavFeedback] = useState<{ type: 'success' | 'warning'; text: string } | null>(null);

  React.useEffect(() => {
    if (settings.navVisibility) {
      const isMyWebActive =
        settings.navVisibility.myWeb !== false &&
        (settings.navVisibility as any).myweb !== false;
      setNavVisibility({
        garden: settings.navVisibility.garden !== false,
        blog: settings.navVisibility.blog !== false,
        letters: settings.navVisibility.letters !== false,
        animations: settings.navVisibility.animations !== false,
        myWeb: isMyWebActive,
        myweb: isMyWebActive,
      });
    }
  }, [settings.navVisibility]);

  const handleToggleNavVisibility = (key: keyof NavVisibilitySettings) => {
    const activeCount = [
      navVisibility.garden,
      navVisibility.blog,
      navVisibility.letters,
      navVisibility.animations,
      navVisibility.myWeb,
    ].filter(Boolean).length;

    // Safety: ensure at least one interface remains visible
    if (navVisibility[key] && activeCount <= 1) {
      setNavFeedback({
        type: 'warning',
        text: '¡Atención! Debe haber al menos 1 interfaz visible para que los visitantes puedan navegar.',
      });
      setTimeout(() => setNavFeedback(null), 4000);
      return;
    }

    const newVal = !navVisibility[key];
    const updated: NavVisibilitySettings = {
      ...navVisibility,
      [key]: newVal,
    };
    if (key === 'myWeb' || (key as string) === 'myweb') {
      updated.myWeb = newVal;
      updated.myweb = newVal;
    }
    setNavVisibility(updated);

    const updatedSettings: CoupleSettings = {
      ...settings,
      navVisibility: updated,
    };
    onSaveSettings(updatedSettings);
    romanticAudio.playChime();

    const nameMap: Record<keyof NavVisibilitySettings, string> = {
      garden: 'Jardín',
      blog: 'Álbum',
      letters: 'Cartas',
      animations: 'Frases',
      myWeb: 'Mi Web',
      myweb: 'Mi Web',
    };

    setNavFeedback({
      type: 'success',
      text: `Interfaz "${nameMap[key] || 'Mi Web'}" ${newVal ? 'ahora está activada y visible' : 'ha sido ocultada'} en la barra de navegación pública.`,
    });
    setTimeout(() => setNavFeedback(null), 3500);
  };

  const handleSetAllNavVisible = (showAll: boolean) => {
    const updated: NavVisibilitySettings = {
      garden: true, // Garden is kept active as base
      blog: showAll,
      letters: showAll,
      animations: showAll,
      myWeb: showAll,
      myweb: showAll,
    };
    setNavVisibility(updated);

    const updatedSettings: CoupleSettings = {
      ...settings,
      navVisibility: updated,
    };
    onSaveSettings(updatedSettings);
    romanticAudio.playChime();

    setNavFeedback({
      type: 'success',
      text: showAll
        ? 'Todas las 4 interfaces públicas han sido activadas y están visibles.'
        : 'Solo el Jardín está visible; las demás interfaces se han ocultado.',
    });
    setTimeout(() => setNavFeedback(null), 3500);
  };

  // Toast message
  const [toast, setToast] = useState('');

  // Confirmation modal dialog for delete and publish actions
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'publish' | 'warning';
    previewImage?: string;
    previewDetails?: {
      date?: string;
      place?: string;
      message?: string;
      mediaCount?: number;
    };
    onConfirm: () => void;
  } | null>(null);

  const triggerToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  // Dashboard Quick Search query state
  const [dashboardSearchQuery, setDashboardSearchQuery] = useState('');

  // Quick Action Handlers for Modern Dashboard
  const handleToggleQuickMaintenance = async () => {
    const current = Boolean(
      settings.webMaintenance?.isEnabled ??
      settings.webMaintenance?.maintenanceEnabled ??
      (settings as any).maintenanceEnabled
    );
    try {
      await updateWebMaintenanceStatusInDb(!current, {
        title: settings.webMaintenance?.title || settings.webMaintenance?.maintenanceTitle,
        message: settings.webMaintenance?.message || settings.webMaintenance?.maintenanceMessage,
        returnTime: settings.webMaintenance?.estimatedReturn || settings.webMaintenance?.maintenanceReturnTime,
      });
      triggerToast(!current ? '🛠️ Modo mantenimiento ACTIVADO' : '✅ Web operativa para todos los visitantes');
    } catch {
      triggerToast('Error al actualizar el estado de mantenimiento');
    }
  };

  const handleToggleQuickAppKillSwitch = () => {
    const current = settings.appKillSwitch?.isEnabled || false;
    const updated: CoupleSettings = {
      ...settings,
      appKillSwitch: {
        ...settings.appKillSwitch,
        isEnabled: !current,
        disabledAt: !current ? new Date().toISOString() : undefined,
        disabledBy: activeProfile,
        title: settings.appKillSwitch?.title || 'Actualización Requerida',
        message:
          settings.appKillSwitch?.message ||
          'Por favor actualiza la aplicación a la última versión disponible.',
      },
    };
    onSaveSettings(updated);
    triggerToast(!current ? '📱 Bloqueo de App móvil ACTIVADO' : '✅ App móvil desbloqueada y operativa');
  };

  const handleToggleQuickMusicAutoplay = () => {
    const current = settings.musicAutoplay || false;
    const updated: CoupleSettings = {
      ...settings,
      musicAutoplay: !current,
    };
    onSaveSettings(updated);
    triggerToast(!current ? '🎵 Autoplay de música ACTIVADO' : '🔇 Autoplay desactivado');
  };

  const handleToggleQuickAutoApproval = () => {
    const current = settings.myWebConfig?.autoApproval || false;
    const updated: CoupleSettings = {
      ...settings,
      myWebConfig: {
        whatsappAdminNumber: settings.myWebConfig?.whatsappAdminNumber || '',
        ...settings.myWebConfig,
        autoApproval: !current,
      },
    };
    onSaveSettings(updated);
    triggerToast(!current ? '🌟 Auto-aprobación de solicitudes ACTIVADA' : '⏳ Aprobación manual requerida');
  };

  const handleQuickWaterGarden = () => {
    const nowIso = new Date().toISOString();
    const updated: CoupleSettings = {
      ...settings,
      gardenWatering: {
        lastWateredAt: nowIso,
        wateringIntervalDays: settings.gardenWatering?.wateringIntervalDays || 3,
        wateredCount: (settings.gardenWatering?.wateredCount || 0) + 1,
        lastWateredBy: activeProfile === 'novio' ? p1Name || 'El Novio' : p2Name || 'La Novia',
        canWaterLevel: 100,
        lastWaterRefillDate: nowIso.split('T')[0],
        witheringEnabled: settings.gardenWatering?.witheringEnabled !== false,
      },
    };
    onSaveSettings(updated);
    triggerToast('💧 ¡Jardín regado al 100% de hidratación!');
  };

  const handleToggleQuickAtmosphere = () => {
    const isAuto = (settings.atmosphereMode || 'auto') === 'auto';
    const updated: CoupleSettings = {
      ...settings,
      atmosphereMode: isAuto ? 'manual' : 'auto',
    };
    onSaveSettings(updated);
    triggerToast(!isAuto ? '☀️ Ciclo solar y reloj real ACTIVADO' : '🎨 Modo de cielo manual activado');
  };

  // Open letter audit details (since uploaded letters cannot be edited)
  const handleOpenEditLetterModal = (letter: DailyLetter) => {
    setSelectedAuditLetter(letter);
    romanticAudio.playNote(440, 0.2, 'sine', 0.05);
    triggerToast('🔒 Las cartas ya subidas son inmutables. Aquí puedes mirar la fecha, hora y quién la abrió.');
  };

  // Toggle unlock state for letter
  const handleToggleUnlockLetter = (letterId: string) => {
    const target = lettersList.find((l) => l.id === letterId);
    if (target && isLetterSealed(target)) {
      romanticAudio.playNote(330, 0.4, 'sine', 0.05);
      triggerToast('🔒 Esta carta está sellada bajo candado y se abrirá automáticamente en la fecha acordada.');
      return;
    }

    const updated = lettersList.map((l) => {
      if (l.id === letterId) {
        const nextState = !l.isUnlocked;
        return { ...l, isUnlocked: nextState };
      }
      return l;
    });
    handleLettersUpdate(updated);
    romanticAudio.playChime();
    triggerToast('Estado de desbloqueo de carta actualizado.');
  };

  // Toggle public / private state for letter (cartas existentes o nuevas)
  const handleToggleLetterPrivacy = (letterId: string) => {
    let nextIsPrivate = false;
    let letterTitle = '';
    const updated = lettersList.map((l) => {
      if (l.id === letterId) {
        nextIsPrivate = !l.isPrivate;
        letterTitle = l.title;
        return { ...l, isPrivate: nextIsPrivate };
      }
      return l;
    });

    handleLettersUpdate(updated);

    // Also sync selected audit letter if open
    if (selectedAuditLetter && selectedAuditLetter.id === letterId) {
      setSelectedAuditLetter({ ...selectedAuditLetter, isPrivate: nextIsPrivate });
    }

    romanticAudio.playChime();
    triggerToast(
      nextIsPrivate
        ? `🔒 "${letterTitle}" ahora es Privada (requiere PIN).`
        : `🌐 "${letterTitle}" ahora es Pública (visible para todos).`
    );
  };

  // Duplicate letter
  const handleDuplicateLetter = (letter: DailyLetter) => {
    const copy: DailyLetter = {
      ...letter,
      id: `letter-${Date.now()}`,
      title: `${letter.title} (Copia)`,
      createdAt: new Date().toISOString(),
    };
    const updated = [copy, ...lettersList];
    handleLettersUpdate(updated);
    romanticAudio.playChime();
    triggerToast('Carta duplicada exitosamente.');
  };

  // Delete letter with confirmation dialog
  const handleDeleteLetter = (letterId: string) => {
    const letter = lettersList.find((l) => l.id === letterId);
    setConfirmDialog({
      isOpen: true,
      title: '¿Eliminar carta diaria?',
      description: `¿Confirmas que deseas eliminar permanentemente la carta "${letter?.title || 'seleccionada'}"?`,
      type: 'danger',
      confirmText: 'Sí, eliminar carta',
      onConfirm: () => {
        if (onDeleteDailyLetter) {
          onDeleteDailyLetter(letterId);
        } else {
          const updated = lettersList.filter((l) => l.id !== letterId);
          handleLettersUpdate(updated);
        }
        triggerToast('Carta diaria eliminada.');
        setConfirmDialog(null);
      },
    });
  };



  // Step 2: Select Profile (Novio or Novia) and enter dashboard locked
  const handleSelectProfileAndEnter = (profile?: 'novio' | 'novia') => {
    romanticAudio.playBloomSuccess();
    onLogin(profile || activeAdminProfile || 'novio');
  };

  // Helper for computing profile birthday, age and days remaining
  const getProfileBirthdayInfo = (birthDateString?: string) => {
    if (!birthDateString) return null;
    const parts = birthDateString.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

    const now = new Date();
    let age = now.getFullYear() - year;
    const m = now.getMonth() + 1 - month;
    if (m < 0 || (m === 0 && now.getDate() < day)) {
      age--;
    }

    const cd = getNextEventCountdown(month, day);
    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return {
      age: Math.max(0, age),
      countdown: cd,
      formattedBirthday: `${day} de ${monthNames[month - 1] || ''}`,
    };
  };

  // Upload and permanently save profile avatar (novio or novia)
  const handleUploadAvatar = async (profile: 'novio' | 'novia', file: File) => {
    if (!file.type.startsWith('image/')) {
      triggerToast('Por favor selecciona un archivo de imagen válido (JPG, PNG, WebP).');
      return;
    }
    const isNovio = profile === 'novio';
    if (isNovio) {
      setIsSavingAvatar1(true);
      setImgErrorP1(false);
    } else {
      setIsSavingAvatar2(true);
      setImgErrorP2(false);
    }
    try {
      // Compress to ideal avatar proportions (360x360 max, 85% quality, ~25KB)
      // This prevents exceeding Firestore document limits and ensures permanent persistence
      const compressed = await compressImageFile(file, {
        maxDimension: 360,
        quality: 0.85,
        targetMime: 'image/jpeg',
      });
      if (isNovio) {
        setP1Avatar(compressed);
        setImgErrorP1(false);
      } else {
        setP2Avatar(compressed);
        setImgErrorP2(false);
      }

      // Save directly to Firestore and local storage immediately
      await saveProfileAvatarToDb(profile, compressed);

      // Sync settings state
      onSaveSettings({
        ...settings,
        [isNovio ? 'partner1Avatar' : 'partner2Avatar']: compressed,
      });

      triggerToast(`Foto de perfil de ${isNovio ? (p1Name || 'El Novio') : (p2Name || 'La Novia')} guardada con éxito en la nube.`);
      romanticAudio.playBloomSuccess();
    } catch (err) {
      console.error(`Error guardando foto de ${profile}:`, err);
      triggerToast('Error al comprimir o guardar la foto de perfil. Intenta con otra imagen.');
    } finally {
      if (isNovio) setIsSavingAvatar1(false);
      else setIsSavingAvatar2(false);
    }
  };

  // Save profile avatar from web URL
  const handleSaveAvatarFromUrl = async (profile: 'novio' | 'novia', url: string) => {
    const trimmed = url.trim();
    if (!trimmed) {
      triggerToast('Ingresa una dirección URL válida.');
      return;
    }
    const isNovio = profile === 'novio';
    if (isNovio) {
      setP1Avatar(trimmed);
      setImgErrorP1(false);
      setShowUrlInput1(false);
      setUrlInput1('');
    } else {
      setP2Avatar(trimmed);
      setImgErrorP2(false);
      setShowUrlInput2(false);
      setUrlInput2('');
    }
    try {
      await saveProfileAvatarToDb(profile, trimmed);
      onSaveSettings({
        ...settings,
        [isNovio ? 'partner1Avatar' : 'partner2Avatar']: trimmed,
      });
      triggerToast(`Foto de perfil de ${isNovio ? (p1Name || 'El Novio') : (p2Name || 'La Novia')} actualizada.`);
      romanticAudio.playBloomSuccess();
    } catch (err) {
      console.error(`Error guardando URL de foto de ${profile}:`, err);
      triggerToast('Error al guardar la URL de la foto.');
    }
  };

  // Remove profile avatar
  const handleRemoveAvatar = async (profile: 'novio' | 'novia') => {
    const isNovio = profile === 'novio';
    if (isNovio) {
      setP1Avatar('');
      setImgErrorP1(false);
    } else {
      setP2Avatar('');
      setImgErrorP2(false);
    }
    try {
      await saveProfileAvatarToDb(profile, '');
      onSaveSettings({
        ...settings,
        [isNovio ? 'partner1Avatar' : 'partner2Avatar']: '',
      });
      triggerToast(`Foto de perfil de ${isNovio ? 'El Novio' : 'La Novia'} eliminada.`);
    } catch (err) {
      console.error(`Error quitando avatar de ${profile}:`, err);
    }
  };

  // Discard uncommitted form changes
  const handleResetCoupleForm = () => {
    setP1Name(settings.partner1Name);
    setP2Name(settings.partner2Name);
    setP1Title(settings.partner1Title || 'El Novio');
    setP2Title(settings.partner2Title || 'La Novia');
    setP1BirthDate(settings.partner1BirthDate || '');
    setP2BirthDate(settings.partner2BirthDate || '');
    setP1BloodType(settings.partner1BloodType || 'O+');
    setP2BloodType(settings.partner2BloodType || 'O+');
    setP1Avatar(settings.partner1Avatar || '');
    setP2Avatar(settings.partner2Avatar || '');
    setImgErrorP1(false);
    setImgErrorP2(false);
    setShowUrlInput1(false);
    setShowUrlInput2(false);
    setAnnivDate(settings.startDate ? settings.startDate.split('T')[0] : '');
    setRomanticQuote(settings.quote);
    triggerToast('Valores de perfiles restablecidos.');
  };

  // Save couple settings including both individual profiles with birth dates and blood types
  const handleSaveCouple = (e: React.FormEvent) => {
    e.preventDefault();
    const fullIso = `${annivDate}T00:00:00`;
    const resolvedP1Avatar = p1Avatar.trim() || settings.partner1Avatar || '';
    const resolvedP2Avatar = p2Avatar.trim() || settings.partner2Avatar || '';

    const updated: CoupleSettings = {
      ...settings,
      partner1Name: p1Name.trim() || 'Nosotros',
      partner2Name: p2Name.trim() || 'Siempre',
      partner1BirthDate: p1BirthDate.trim(),
      partner2BirthDate: p2BirthDate.trim(),
      partner1BloodType: p1BloodType.trim() || 'O+',
      partner2BloodType: p2BloodType.trim() || 'O+',
      partner1Title: p1Title.trim() || 'El Novio',
      partner2Title: p2Title.trim() || 'La Novia',
      partner1Avatar: resolvedP1Avatar,
      partner2Avatar: resolvedP2Avatar,
      startDate: fullIso,
      quote: romanticQuote.trim() || 'Cada segundo a tu lado es un regalo 🧡',
      atmosphereMode,
      manualTimeOfDay,
      manualWeather,
      manualMinuteOfDay,
    };
    onSaveSettings(updated);
    romanticAudio.playChime();
    triggerToast('¡Perfiles de Novio y Novia, fecha de nacimiento, tipo de sangre y fotos guardados!');
  };

  // Save Atmosphere Settings
  const handleSaveAtmosphere = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const updated: CoupleSettings = {
      ...settings,
      atmosphereMode,
      manualTimeOfDay,
      manualWeather,
      manualMinuteOfDay,
    };
    onSaveSettings(updated);
    romanticAudio.playBloomSuccess();
    triggerToast('¡Configuración de ambiente y cielo del jardín guardada!');
  };

  // Reset to auto real-time atmosphere
  const handleResetToAutoAtmosphere = () => {
    setAtmosphereMode('auto');
    const updated: CoupleSettings = {
      ...settings,
      atmosphereMode: 'auto',
    };
    onSaveSettings(updated);
    romanticAudio.playChime();
    triggerToast('¡Modo automático en tiempo real activado!');
  };

  // Update Novio Security (Email & Password)
  const handleUpdateNovioSecurity = async (e: React.FormEvent) => {
    e.preventDefault();
    setP1Error('');
    setP1Success('');

    const cleanEmail = p1EmailInput.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setP1Error('Por favor ingresa un correo electrónico válido para el Novio.');
      return;
    }

    if (p1NewPass) {
      if (p1NewPass.length < 6) {
        setP1Error('La nueva contraseña del Novio debe tener al menos 6 caracteres.');
        return;
      }
      if (p1NewPass !== p1ConfirmPass) {
        setP1Error('Las contraseñas no coinciden.');
        return;
      }
    }

    setP1Loading(true);
    try {
      if (p1NewPass) {
        if (auth.currentUser) {
          try {
            await updatePassword(auth.currentUser, p1NewPass.trim());
          } catch (passErr: any) {
            console.warn('Firebase Auth direct password update note:', passErr);
          }
        }
      }

      const updated: CoupleSettings = {
        ...settings,
        partner1Email: cleanEmail,
      };

      onSaveSettings(updated);
      configurePartnerEmails(cleanEmail, p2EmailInput.trim().toLowerCase());

      setP1NewPass('');
      setP1ConfirmPass('');
      setP1Success('¡Credenciales de Novio actualizadas exitosamente! Entrará directo a su perfil 💙');
      romanticAudio.playBloomSuccess();
      triggerToast('¡Acceso del Novio guardado!');
    } catch (err: any) {
      setP1Error(err?.message || 'Error al actualizar credenciales de Novio.');
    } finally {
      setP1Loading(false);
    }
  };

  // Update Novia Security (Email & Password, with Firebase Auth independent account creation)
  const handleUpdateNoviaSecurity = async (e: React.FormEvent) => {
    e.preventDefault();
    setP2Error('');
    setP2Success('');

    const cleanEmail = p2EmailInput.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setP2Error('Por favor ingresa un correo electrónico válido para la Novia.');
      return;
    }

    if (p2NewPass) {
      if (p2NewPass.length < 6) {
        setP2Error('La nueva contraseña de la Novia debe tener al menos 6 caracteres.');
        return;
      }
      if (p2NewPass !== p2ConfirmPass) {
        setP2Error('Las contraseñas de la Novia no coinciden.');
        return;
      }
    }

    setP2Loading(true);
    try {
      if (p2NewPass) {
        // Provision or sync secondary Firebase Auth account for Novia without breaking current session
        const authResult = await createPartnerAuthAccount(cleanEmail, p2NewPass);
        if (!authResult.success && !authResult.alreadyExists) {
          console.warn('Firebase Auth secondary creation notice:', authResult.error);
        }
      }

      const updated: CoupleSettings = {
        ...settings,
        partner2Email: cleanEmail,
      };

      onSaveSettings(updated);
      configurePartnerEmails(p1EmailInput.trim().toLowerCase(), cleanEmail);

      setP2NewPass('');
      setP2ConfirmPass('');
      setP2Success('¡Acceso personal de la Novia configurado exitosamente! Entrará directo a su perfil 💖');
      romanticAudio.playBloomSuccess();
      triggerToast('¡Acceso de la Novia guardado!');
    } catch (err: any) {
      setP2Error(err?.message || 'Error al actualizar credenciales de la Novia.');
    } finally {
      setP2Loading(false);
    }
  };

  // Crear o vincular Cuentas Administrativas (Novio o Novia)
  const handleCreateAdminAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');

    const cleanEmail = createEmail.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setCreateError('Por favor ingresa un correo electrónico válido.');
      return;
    }

    if (createPassword.trim().length < 6) {
      setCreateError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (createPassword !== createConfirmPassword) {
      setCreateError('Las contraseñas no coinciden.');
      return;
    }

    setCreateLoading(true);
    try {
      // 1. Provision or link account in Firebase Auth via authoritative API (with client fallback)
      const authResult = await createPartnerAuthAccount(cleanEmail, createPassword.trim(), createRole);
      if (!authResult.success) {
        setCreateError(authResult.error || 'Error al procesar la cuenta en Firebase Auth.');
        setCreateLoading(false);
        return;
      }

      // 2. Authoritative real UID from Firebase Authentication
      const targetUid = authResult.uid;
      if (!targetUid) {
        setCreateError('No se pudo obtener el UID de Firebase. Intenta nuevamente.');
        setCreateLoading(false);
        return;
      }

      // 3. Immediate local state reflection for zero-latency UI update
      if (createRole === 'novia') {
        setLocalNoviaEmail(cleanEmail);
        setLocalNoviaUid(targetUid);
        setP2EmailInput(cleanEmail);
      } else {
        setLocalNovioEmail(cleanEmail);
        setLocalNovioUid(targetUid);
        setP1EmailInput(cleanEmail);
      }

      // 4. Update settings structure (single source of truth in couple_data/public)
      let updated: CoupleSettings;
      if (createRole === 'novia') {
        updated = {
          ...settings,
          partner2Email: cleanEmail,
          partner2Uid: targetUid,
        };
      } else {
        updated = {
          ...settings,
          partner1Email: cleanEmail,
          partner1Uid: targetUid,
        };
      }

      // 5. Save to Firestore (Awaited with error handling)
      await onSaveSettings(updated);

      // 6. Register in /admins/{uid} for Firestore rules verification
      await registerAdminRecordInDb(targetUid, { email: cleanEmail, role: createRole });

      if (createRole === 'novia') {
        configurePartnerEmails(settings.partner1Email, cleanEmail);
        setCreateSuccess(
          authResult.alreadyExists
            ? `¡Cuenta de la Novia sincronizada y asociada exitosamente! Podrá entrar directamente a su perfil de Novia 💕 con ${cleanEmail}.`
            : `¡Cuenta para la Novia configurada exitosamente! Podrá entrar directamente a su perfil de Novia 💕 con ${cleanEmail}.`
        );
      } else {
        configurePartnerEmails(cleanEmail, settings.partner2Email);
        setCreateSuccess(
          authResult.alreadyExists
            ? `¡Cuenta del Novio sincronizada y asociada exitosamente! Podrá entrar directamente a su perfil de Novio 💙 con ${cleanEmail}.`
            : `¡Cuenta para el Novio configurada exitosamente! Podrá entrar directamente a su perfil de Novio 💙 con ${cleanEmail}.`
        );
      }

      romanticAudio.playBloomSuccess();
      triggerToast('¡Cuenta administrativa configurada y vinculada correctamente!');

      // Reset form fields
      setCreateEmail('');
      setCreatePassword('');
      setCreateConfirmPassword('');
      setShowConfigForm(false);
    } catch (err: any) {
      setCreateError(
        err?.message || 'No se pudo completar la configuración del perfil. La cuenta no quedó completamente configurada. Intenta nuevamente.'
      );
    } finally {
      setCreateLoading(false);
    }
  };

  // Sincronizar o Reparar Cuenta Existente de Perfil (Requirement 25)
  const handleRepairPartnerAccount = async (role: 'novio' | 'novia', emailOverride?: string) => {
    setRepairLoading(true);
    setRepairError('');
    setRepairSuccess('');
    const targetEmail = (
      emailOverride ||
      (role === 'novia' ? p2EmailInput || settings.partner2Email : p1EmailInput || settings.partner1Email) ||
      ''
    ).trim().toLowerCase();

    if (!targetEmail || !targetEmail.includes('@')) {
      setRepairError('Por favor ingresa un correo electrónico válido para sincronizar.');
      setRepairLoading(false);
      return;
    }

    try {
      const res = await syncOrRegisterPartnerAccount(targetEmail, role);
      if (!res.success) {
        setRepairError(res.error || 'No fue posible sincronizar la cuenta con Firebase.');
        setRepairLoading(false);
        return;
      }

      const updated: CoupleSettings = {
        ...settings,
        ...(role === 'novia'
          ? { partner2Email: res.email, partner2Uid: res.uid }
          : { partner1Email: res.email, partner1Uid: res.uid }),
      };

      await onSaveSettings(updated);

      if (res.uid) {
        await registerAdminRecordInDb(res.uid, { email: res.email, role });
      }

      if (role === 'novia') {
        setP2EmailInput(res.email);
        setRepairSuccess(`¡Cuenta de Novia 💕 sincronizada y configurada correctamente con ${res.email}! Estado: Activa.`);
      } else {
        setP1EmailInput(res.email);
        setRepairSuccess(`¡Cuenta de Novio 💙 sincronizada y configurada correctamente con ${res.email}! Estado: Activa.`);
      }

      romanticAudio.playBloomSuccess();
      triggerToast('¡Cuenta de perfil sincronizada exitosamente!');
      setTimeout(() => {
        setRepairModal(null);
      }, 1500);
    } catch (err: any) {
      setRepairError(err?.message || 'Error al guardar la sincronización del perfil en Firestore.');
    } finally {
      setRepairLoading(false);
    }
  };

  // Enviar código OTP de restablecimiento de contraseña desde el panel admin
  const handleSendResetEmailFromAdmin = async (email: string, roleName: string) => {
    if (!email) return;
    try {
      const res = await requestPasswordResetCode(email);
      if (res.success) {
        romanticAudio.playBloomSuccess();
        triggerToast(`Código enviado a ${email}`);
        setCreateSuccess(`Se ha enviado un código de verificación a ${email} (${roleName}).`);
      } else {
        setCreateError(res.error || 'No pudimos procesar la solicitud. Inténtalo nuevamente.');
      }
    } catch {
      setCreateError('No pudimos procesar la solicitud. Inténtalo nuevamente.');
    }
  };

  // Delete memory with confirmation dialog
  const handleDeleteMemory = (id: string) => {
    const mem = memories.find((m) => m.id === id);
    setConfirmDialog({
      isOpen: true,
      title: '¿Eliminar este recuerdo?',
      description: 'Esta acción es permanente y no se puede deshacer. Se eliminará la foto, fecha, notas y todos los comentarios asociados de vuestro álbum.',
      type: 'danger',
      confirmText: 'Sí, eliminar recuerdo',
      previewImage: mem?.mediaUrl,
      previewDetails: mem
        ? {
            place: mem.place,
            date: formatDateSpanish(mem.date),
            message: mem.message,
            mediaCount: mem.mediaUrls?.length || 1,
          }
        : undefined,
      onConfirm: () => {
        if (onDeleteMemory) {
          onDeleteMemory(id);
        } else {
          const updated = memories.filter((m) => m.id !== id);
          onUpdateMemories(updated);
        }
        triggerToast('Recuerdo eliminado del álbum.');
        setConfirmDialog(null);
      },
    });
  };

  // Save edited memory
  const handleSaveEditedMemory = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMemory) return;
    const updated = memories.map((m) => (m.id === editingMemory.id ? editingMemory : m));
    onUpdateMemories(updated);
    setEditingMemory(null);
    romanticAudio.playChime();
    triggerToast('¡Recuerdo modificado con éxito!');
  };

  // Export JSON
  const handleExportBackup = () => {
    // Exclude security credentials from backup export for complete privacy
    const safeSettings = { ...settings };
    const backupData = {
      version: 4,
      exportedAt: new Date().toISOString(),
      settings: {
        ...safeSettings,
        partner1Name: p1Name,
        partner2Name: p2Name,
        startDate: `${annivDate}T00:00:00`,
        quote: romanticQuote,
      },
      memories,
      dailyLetters: lettersList,
      specialEvents: lettersList,
    };
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `respaldo_para_ti_mvd_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    triggerToast('Archivo de respaldo descargado.');
  };

  // Import JSON
  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        if (parsed.settings && Array.isArray(parsed.memories)) {
          const importedLetters = parsed.dailyLetters || parsed.specialEvents || lettersList;
          onImportData({
            settings: parsed.settings,
            memories: parsed.memories,
            dailyLetters: importedLetters,
            specialEvents: importedLetters,
          });
          triggerToast('¡Copia de seguridad restaurada correctamente!');
        } else {
          triggerToast('El archivo no tiene el formato de respaldo adecuado.');
        }
      } catch (err) {
        triggerToast('Error al leer el archivo JSON.');
      }
    };
    reader.readAsText(file);
  };

  // Filtered memories list
  const filteredMemories = memories.filter((m) => {
    const matchesCat = filterCat === 'all' || m.category === filterCat;
    const matchesAuthor =
      filterAuthor === 'all' ||
      (filterAuthor === 'novio' && (m.authorRole === 'novio' || !m.authorRole)) ||
      (filterAuthor === 'novia' && m.authorRole === 'novia');
    const matchesSearch =
      m.place.toLowerCase().includes(searchMemory.toLowerCase()) ||
      m.message.toLowerCase().includes(searchMemory.toLowerCase()) ||
      m.date.includes(searchMemory);
    return matchesCat && matchesAuthor && matchesSearch;
  });

  // Filtered daily letters list
  const todayStr = new Date().toISOString().split('T')[0];
  const filteredLetters = lettersList.filter((letter) => {
    const letterDate = letter.unlockDate || letter.date || '';
    const isUnlocked = letter.isUnlocked || (letterDate ? todayStr >= letterDate : true);
    const matchesCat =
      filterLetterCategory === 'all'
        ? true
        : filterLetterCategory === 'unlocked'
        ? isUnlocked
        : filterLetterCategory === 'locked'
        ? !isUnlocked
        : letter.category === filterLetterCategory;

    const matchesSearch =
      letter.title.toLowerCase().includes(searchLetter.toLowerCase()) ||
      letter.subtitle.toLowerCase().includes(searchLetter.toLowerCase()) ||
      (letter.letterContent?.bodyParagraphs || []).some((p) =>
        p.toLowerCase().includes(searchLetter.toLowerCase())
      ) ||
      (letter.letterContent?.recipientName || '').toLowerCase().includes(searchLetter.toLowerCase());

    return matchesCat && matchesSearch;
  });

  const unlockedLettersCount = lettersList.filter(
    (e) => e.isUnlocked || (e.unlockDate || e.date ? todayStr >= (e.unlockDate || e.date)! : true)
  ).length;

  // Calculate live preview time
  const currentTime = calculateTimeElapsed(`${annivDate}T00:00:00`);

  // Calculate nearest romantic milestone
  const nearestEvent = React.useMemo(() => {
    const list: Array<ReturnType<typeof getNextEventCountdown> & { name: string; icon: string }> = [];

    if (settings.startDate) {
      const parsedAnniv = new Date(settings.startDate);
      if (!isNaN(parsedAnniv.getTime())) {
        list.push({
          name: 'Aniversario Oficial',
          icon: '💍',
          ...getNextEventCountdown(parsedAnniv.getMonth() + 1, parsedAnniv.getDate()),
        });
      }
    }

    if (settings.partner2BirthDate) {
      const parsedP2B = new Date(settings.partner2BirthDate);
      if (!isNaN(parsedP2B.getTime())) {
        list.push({
          name: `Cumpleaños de ${settings.partner2Name || 'Pareja 2'}`,
          icon: '🎂',
          ...getNextEventCountdown(parsedP2B.getMonth() + 1, parsedP2B.getDate()),
        });
      }
    }

    if (settings.partner1BirthDate) {
      const parsedP1B = new Date(settings.partner1BirthDate);
      if (!isNaN(parsedP1B.getTime())) {
        list.push({
          name: `Cumpleaños de ${settings.partner1Name || 'Pareja 1'}`,
          icon: '🎉',
          ...getNextEventCountdown(parsedP1B.getMonth() + 1, parsedP1B.getDate()),
        });
      }
    }

    list.push(
      { name: 'Flores Amarillas', icon: '🌻', ...getNextEventCountdown(9, 21) },
      { name: 'San Valentín', icon: '❤️', ...getNextEventCountdown(2, 14) }
    );

    list.sort((a, b) => a.daysRemaining - b.daysRemaining);
    return list[0];
  }, [settings.partner1Name, settings.partner2Name, settings.startDate, settings.partner1BirthDate, settings.partner2BirthDate]);

  // ==========================================
  // VIEW 1: AUTHENTICATION LOGIN PORTAL
  // ==========================================
  if (!isAuthenticated) {
    return (
      <ModernAdminLogin
        settings={settings}
        p1Name={p1Name}
        p2Name={p2Name}
        p1Title={p1Title}
        p2Title={p2Title}
        p1Avatar={p1Avatar}
        p2Avatar={p2Avatar}
        onNavigateToPublic={onNavigateToPublic}
        onSelectProfileAndEnter={handleSelectProfileAndEnter}
      />
    );
  }

  // ==========================================
  // VIEW 2: AUTHENTICATED ADMIN DASHBOARD
  // ==========================================
  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800 flex flex-col font-['Plus_Jakarta_Sans',sans-serif] relative selection:bg-rose-100 selection:text-rose-900">
      {/* MODERN REDESIGNED ADMIN HEADER & QUICK NAVIGATION */}
      <ModernAdminNav
        activeSection={activeSection}
        onSelectSection={handleSelectSection}
        settings={settings}
        activeProfile={activeProfile}
        p1Name={p1Name}
        p2Name={p2Name}
        p1Avatar={p1Avatar}
        p2Avatar={p2Avatar}
        visitorLogs={visitorLogs}
        memories={memories}
        lettersList={lettersList}
        webRequests={webRequests}
        onNavigateToPublic={onNavigateToPublic}
        onLogout={onLogout}
        copyRouteLink={copyRouteLink}
        toast={toast}
        onCloseToast={() => setToast('')}
        isUserWeb={isUserWeb}
        userWebOrderCode={userWebOrderCode}
      />

      {/* MAIN ADMIN WORKSPACE - ISOLATED FULL WIDTH VIEWS */}
      <div className="max-w-7xl mx-auto w-full flex-1 p-3 sm:p-6 lg:p-8">
        <main className="w-full space-y-6">
          {/* ======================================================== */}
          {/* SECTION: DASHBOARD / RESUMEN GENERAL (/admin) */}
          {/* ======================================================== */}
          {activeSection === 'dashboard' && (
            <ModernAdminDashboard
              settings={settings}
              memories={memories}
              lettersList={lettersList}
              visitorLogs={visitorLogs}
              webRequests={webRequests}
              activeProfile={activeProfile}
              p1Name={p1Name}
              p2Name={p2Name}
              p1Avatar={p1Avatar}
              p2Avatar={p2Avatar}
              onSelectSection={handleSelectSection}
              onOpenUploadModal={onOpenUploadModal || (() => {})}
              onNavigateToPublic={onNavigateToPublic}
              onSaveSettings={onSaveSettings}
              onEditMemory={(mem) => {
                setEditingMemory(mem);
                handleSelectSection('memories');
              }}
              triggerToast={triggerToast}
              isUserWeb={isUserWeb}
            />
          )}

          {/* ======================================================== */}
          {/* SECTION: CARTAS DE AMOR (/admin/cartas) */}
          {/* ======================================================== */}
          {activeSection === 'letters' && (
            <div className="space-y-4">
              {/* Header Action Bar */}
              {renderSectionBreadcrumbHeader(
                'Administrador de Cartas 💌',
                'Gestiona cartas de amor públicas y privadas con sellos de cera, música romántica y auditoría de lectura.',
                'letters'
              )}

              {/* Search & Filter Toolbar */}
              <div className="bg-white p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="relative w-full sm:w-80">
                  <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar carta diaria..."
                    value={searchLetter}
                    onChange={(e) => setSearchLetter(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 bg-slate-50/80 border border-slate-200/90 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100 transition-all"
                  />
                  {searchLetter && (
                    <button
                      type="button"
                      onClick={() => setSearchLetter('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-1 bg-slate-100/90 p-1 rounded-xl overflow-x-auto w-full sm:w-auto scrollbar-none">
                  {[
                    { id: 'all', label: 'Todas' },
                    { id: 'unlocked', label: '🔓 Abiertas' },
                    { id: 'locked', label: '🔒 Selladas' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setFilterLetterCategory(tab.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                        filterLetterCategory === tab.id
                          ? 'bg-white text-slate-900 font-bold shadow-xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Letters Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredLetters.map((letter) => {
                  const letterDate = letter.unlockDate || letter.date || '';
                  const isLocked = isLetterSealed(letter);
                  return (
                    <div
                      key={letter.id}
                      className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-xs hover:border-slate-300 hover:shadow-md transition-all flex flex-col justify-between"
                    >
                      {/* Letter Banner Image */}
                      <div className="relative h-36 w-full bg-slate-100 overflow-hidden">
                        {letter.coverImage ? (
                          <img
                            src={letter.coverImage}
                            alt={letter.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-r from-rose-100/60 via-amber-100/40 to-pink-100/60 flex items-center justify-center">
                            <span className="text-3xl opacity-40">💌</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                        {/* Letter badge */}
                        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 flex-wrap">
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-white/90 backdrop-blur-xs text-slate-800 shadow-2xs flex items-center gap-1">
                            <span>💌 Carta</span>
                          </span>
                          {letter.isPrivate ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleLetterPrivacy(letter.id);
                              }}
                              className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-900/70 text-purple-200 backdrop-blur-md flex items-center gap-1 transition-all cursor-pointer shadow-2xs hover:bg-purple-900/90"
                              title="Click para cambiar a Carta Pública"
                            >
                              <Lock className="w-2.5 h-2.5" /> Privada
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleLetterPrivacy(letter.id);
                              }}
                              className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-900/70 text-emerald-200 backdrop-blur-md flex items-center gap-1 transition-all cursor-pointer shadow-2xs hover:bg-emerald-900/90"
                              title="Click para cambiar a Carta Privada"
                            >
                              <Globe className="w-2.5 h-2.5" /> Pública
                            </button>
                          )}
                        </div>

                        {/* Status Badge */}
                        <div className="absolute top-2.5 right-2.5">
                          {isLocked ? (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-500/90 text-white backdrop-blur-md flex items-center gap-1 shadow-2xs">
                              <Lock className="w-3 h-3" /> Sellada
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-500/90 text-white backdrop-blur-md flex items-center gap-1 shadow-2xs">
                              <Unlock className="w-3 h-3" /> Abierta
                            </span>
                          )}
                        </div>

                        {/* Title overlay */}
                        <div className="absolute bottom-2.5 left-3 right-3 text-white">
                          <span className="text-[10px] text-amber-200 font-mono flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-amber-300" />
                            {formatDateSpanish(letter.unlockDate || letter.date || '')}
                          </span>
                          <h3 className="text-base font-bold text-white truncate drop-shadow-xs">
                            {letter.title}
                          </h3>
                        </div>
                      </div>

                      {/* Letter Details Body */}
                      <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                        <div>
                          <p className="text-xs text-slate-600 line-clamp-2">
                            {letter.subtitle}
                          </p>

                          {/* Letter snippet preview */}
                          {letter.letterContent && (
                            <div className="mt-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-[11px] text-slate-600 space-y-1">
                              <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono">
                                <span>De: <strong>{letter.letterContent.senderName || 'Nosotros'}</strong></span>
                                <span>Para: <strong>{letter.letterContent.recipientName || 'Siempre'}</strong></span>
                              </div>
                              {isLocked ? (
                                <p className="italic text-amber-700 text-[11px] flex items-center gap-1.5 py-0.5 font-mono">
                                  <Lock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                  <span>Contenido sellado hasta el {formatDateSpanish(letterDate)}.</span>
                                </p>
                              ) : (
                                <p className="line-clamp-2 italic text-slate-600">
                                  "{letter.letterContent.bodyParagraphs?.[0] || '...'}"
                                </p>
                              )}
                            </div>
                          )}

                          {/* Read status & viewer */}
                          <div className="mt-2.5">
                            {letter.isRead || letter.lastOpenedAt || letter.firstOpenedAt ? (
                              <div className="p-2.5 rounded-xl bg-emerald-50 border border-emerald-200/80 text-[11px] text-emerald-800 space-y-0.5">
                                <div className="flex items-center justify-between">
                                  <span className="flex items-center gap-1.5 font-bold">
                                    <Eye className="w-3 h-3 text-emerald-600 shrink-0" />
                                    <span>Abierta: {formatDateTimeSpanish(letter.lastOpenedAt || letter.firstOpenedAt || '')}</span>
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedAuditLetter(letter)}
                                    className="text-[10px] text-emerald-700 hover:text-emerald-900 font-medium underline cursor-pointer"
                                  >
                                    Registro
                                  </button>
                                </div>
                                <div className="text-slate-600 pl-4.5 text-[10px] truncate">
                                  Visto por: <strong className="text-slate-800">{letter.openedBy || settings.partner2Name || 'Destinatario'}</strong>
                                  {letter.views?.[0]?.deviceModel ? ` • ${letter.views[0].deviceModel}` : ''}
                                </div>
                              </div>
                            ) : (
                              <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-200/80 text-[11px] text-amber-800 flex items-center justify-between">
                                <span className="flex items-center gap-1.5">
                                  <Mail className="w-3 h-3 text-amber-600 shrink-0" />
                                  <span>Aún no ha sido abierta ✉️</span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setSelectedAuditLetter(letter)}
                                  className="text-[10px] text-amber-700 hover:text-amber-900 font-medium underline cursor-pointer"
                                >
                                  Detalles
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Interactive Surprise badge & Inmutability Notice */}
                        <div className="space-y-1.5 pt-2.5 border-t border-slate-100">
                          <div className="flex items-center justify-between text-[11px] text-slate-500">
                            <span className="flex items-center gap-1 text-slate-700 font-medium">
                              <Gift className="w-3 h-3 text-rose-500" />
                              Sorpresa:{' '}
                              <strong className="text-slate-800 font-semibold">
                                {letter.interactiveSurprise?.type === 'bouquet-yellow'
                                  ? 'Ramo Flores Amarillas'
                                  : letter.interactiveSurprise?.type === 'birthday-cake'
                                  ? 'Pastel con Velas'
                                  : letter.interactiveSurprise?.type === 'fireworks'
                                  ? 'Fuegos Artificiales'
                                  : letter.interactiveSurprise?.type === 'letter-envelope'
                                  ? 'Carta con Sello'
                                  : 'Sin sorpresa'}
                              </strong>
                            </span>

                            <span className="text-[10px] text-slate-400 font-mono">
                              {isLocked ? '🔒 Sellada' : letter.isUnlocked ? '🔓 Forzada' : '⏰ Abierta'}
                            </span>
                          </div>

                          <div className="text-[10px] text-slate-400 italic flex items-center gap-1">
                            <Shield className="w-3 h-3 text-slate-400" />
                            <span>Carta inmutable protegida</span>
                          </div>
                        </div>

                        {/* Action buttons toolbar */}
                        <div className="pt-2.5 flex items-center justify-between gap-1.5 border-t border-slate-100">
                          {/* Preview modal button */}
                          <button
                            type="button"
                            onClick={() => setPreviewLetter(letter)}
                            className="flex-1 py-1.5 px-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1 transition-all cursor-pointer shadow-2xs active:scale-95"
                            title="Previsualizar sobre de la carta"
                          >
                            <Eye className="w-3.5 h-3.5 text-rose-300" />
                            <span>{isLocked ? 'Ver Sobre 🔒' : 'Abrir Carta'}</span>
                          </button>

                          {/* Toggle unlock */}
                          <button
                            type="button"
                            onClick={() => handleToggleUnlockLetter(letter.id)}
                            disabled={isLocked}
                            className={`p-1.5 rounded-lg text-xs transition-all border ${
                              isLocked
                                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                                : letter.isUnlocked
                                ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 cursor-pointer'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 cursor-pointer'
                            }`}
                            title={
                              isLocked
                                ? 'Carta sellada hasta la fecha acordada'
                                : letter.isUnlocked
                                ? 'Sellar carta'
                                : 'Desbloquear inmediatamente'
                            }
                          >
                            {isLocked ? (
                              <Lock className="w-3.5 h-3.5 text-amber-500" />
                            ) : letter.isUnlocked ? (
                              <Lock className="w-3.5 h-3.5" />
                            ) : (
                              <Unlock className="w-3.5 h-3.5" />
                            )}
                          </button>

                          {/* Toggle Public / Private */}
                          <button
                            type="button"
                            onClick={() => handleToggleLetterPrivacy(letter.id)}
                            className={`p-1.5 rounded-lg text-xs transition-all cursor-pointer border ${
                              letter.isPrivate
                                ? 'bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200'
                                : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200'
                            }`}
                            title={
                              letter.isPrivate
                                ? 'Carta Privada (PIN). Click para cambiar a Pública.'
                                : 'Carta Pública. Click para cambiar a Privada.'
                            }
                          >
                            {letter.isPrivate ? (
                              <Lock className="w-3.5 h-3.5" />
                            ) : (
                              <Globe className="w-3.5 h-3.5" />
                            )}
                          </button>

                          {/* Audit button */}
                          <button
                            type="button"
                            onClick={() => setSelectedAuditLetter(letter)}
                            className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 transition-all cursor-pointer"
                            title="Ver fecha y hora que el usuario abrió la carta y quién la ha visto"
                          >
                            <Clock className="w-3.5 h-3.5" />
                          </button>

                          {/* Duplicate button */}
                          <button
                            type="button"
                            onClick={() => handleDuplicateLetter(letter)}
                            className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-all cursor-pointer"
                            title="Duplicar carta"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>

                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={() => handleDeleteLetter(letter.id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all cursor-pointer"
                            title="Eliminar carta"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {filteredLetters.length === 0 && (
                  <div className="col-span-full bg-white rounded-2xl border border-slate-200/80 p-8 text-center text-slate-500 shadow-xs space-y-2">
                    <Mail className="w-8 h-8 text-slate-300 mx-auto" />
                    <p className="text-sm font-semibold text-slate-800">
                      No se encontraron cartas con los filtros actuales
                    </p>
                    <p className="text-xs text-slate-400">
                      Limpia el buscador o revisa el estado de tus cartas.
                    </p>
                  </div>
                )}
              </div>
              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: RECUERDOS & ÁLBUM (/admin/recuerdos) */}
          {/* ======================================================== */}
          {activeSection === 'memories' && (
            <div className="space-y-4">
              {/* Header Action Bar */}
              {renderSectionBreadcrumbHeader(
                'Administrador de Recuerdos & Álbum 📸',
                'Sube fotos o videos en carrusel, edita mensajes, fechas, lugares y gestiona los recuerdos de la pareja.',
                'memories',
                <button
                  type="button"
                  onClick={onOpenUploadModal}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-white font-semibold text-xs sm:text-sm rounded-xl shadow-xs transition-all active:scale-95 cursor-pointer shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  <span>Subir Nuevo Recuerdo</span>
                </button>
              )}

              {/* Filter Toolbar */}
              <div className="bg-white p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="relative w-full sm:w-80">
                  <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar por lugar, mensaje, fecha..."
                    value={searchMemory}
                    onChange={(e) => setSearchMemory(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-slate-50/80 border border-slate-200/90 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100 transition-all"
                  />
                </div>

                <div className="flex items-center gap-1 bg-slate-100/90 p-1 rounded-xl overflow-x-auto w-full sm:w-auto scrollbar-none">
                  {['all', 'aniversario', 'viaje', 'cita', 'cotidiano', 'especial'].map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setFilterCat(cat)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize whitespace-nowrap transition-all cursor-pointer ${
                        filterCat === cat
                          ? 'bg-white text-slate-900 font-bold shadow-xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {cat === 'all' ? 'Todos' : cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Memories List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredMemories.map((m) => (
                  <div
                    key={m.id}
                    className="bg-white rounded-2xl border border-slate-200/80 hover:border-slate-300 hover:shadow-md transition-all flex flex-col justify-between overflow-hidden shadow-xs group"
                  >
                    <div className="flex gap-3.5 p-3.5 sm:p-4">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-slate-100 shrink-0 relative flex items-center justify-center border border-slate-200/80">
                        <img src={m.mediaUrl} alt={m.place} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        {m.mediaUrls && m.mediaUrls.length > 1 && (
                          <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-slate-900/80 backdrop-blur-xs text-[9px] font-bold text-white">
                            +{m.mediaUrls.length}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-center justify-between gap-1 flex-wrap">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-100">
                              {m.category}
                            </span>
                            {/* Author badge */}
                            <span
                              className={`px-2 py-0.5 rounded-md text-[10px] font-medium flex items-center gap-1 border ${
                                m.authorRole === 'novio'
                                  ? 'bg-sky-50 border-sky-100 text-sky-700'
                                  : m.authorRole === 'novia'
                                  ? 'bg-pink-50 border-pink-100 text-pink-700'
                                  : 'bg-purple-50 border-purple-100 text-purple-700'
                              }`}
                            >
                              <span>
                                {m.authorRole === 'novio'
                                  ? '💙'
                                  : m.authorRole === 'novia'
                                  ? '💖'
                                  : '💑'}
                              </span>
                              <span>
                                {m.authorName ||
                                  (m.authorRole === 'novio'
                                    ? p1Name || settings.partner1Name || 'Novio'
                                    : m.authorRole === 'novia'
                                    ? p2Name || settings.partner2Name || 'Novia'
                                    : 'Ambos')}
                              </span>
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-400 font-mono tabular-nums">
                            {formatDateSpanish(m.date)}
                          </span>
                        </div>
                        <h4 className="font-bold text-sm text-slate-900 truncate flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                          <span>{m.place}</span>
                        </h4>
                        <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                          "{m.message}"
                        </p>
                      </div>
                    </div>

                    <div className="px-3.5 sm:px-4 py-2.5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-xs">
                      <span className="text-[11px] text-slate-500 font-medium flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 text-slate-400 shrink-0" />
                        <span>{formatDateSpanish(m.date)}</span>
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setEditingMemory(m)}
                          className="px-2.5 py-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer text-xs font-semibold shadow-2xs flex items-center gap-1"
                          title="Editar recuerdo"
                        >
                          <Edit3 className="w-3 h-3 text-slate-500" />
                          <span>Editar</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteMemory(m.id)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                          title="Eliminar recuerdo"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredMemories.length === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-8 text-center text-slate-500 shadow-xs space-y-2">
                  <ImageIcon className="w-8 h-8 text-slate-300 mx-auto" />
                  <p className="text-sm font-semibold text-slate-800">No se encontraron recuerdos</p>
                  <p className="text-xs text-slate-400">Intenta con otro término de búsqueda o categoría.</p>
                </div>
              )}

              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: ESTADÍSTICAS DE LA WEB (/admin/estadisticas)    */}
          {/* ======================================================== */}
          {activeSection === 'visitors' && (
            <div className="space-y-4">
              {renderSectionBreadcrumbHeader(
                'Estadísticas de la web 📊',
                'Consulta el tráfico e impacto en tiempo real. Métrica limpia y segura sin recopilación de GPS ni datos invasivos.',
                'visitors',
                <button
                  type="button"
                  onClick={() => onNavigateToPublic('garden')}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/40 text-xs font-bold rounded-xl transition-all cursor-pointer"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Probar en Web Pública</span>
                </button>
              )}
              <VisitorRegistrySection
                visitorLogs={visitorLogs}
                memories={memories}
                settings={settings}
                onDeleteVisitorLog={onDeleteVisitorLog}
                onClearAllVisitorLogs={onClearAllVisitorLogs}
                onBlockIp={onBlockIp}
                onUnblockIp={onUnblockIp}
                activeAdminProfile={activeProfile}
                onTriggerToast={triggerToast}
              />
              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: AMBIENTE & CIELO DEL JARDÍN (/admin/ambiente) */}
          {/* ======================================================== */}
          {activeSection === 'atmosphere' && (
            <div className="space-y-4">
              {renderSectionBreadcrumbHeader(
                'Ambiente, Clima & Ciclo Celestial ☀️🌙',
                'Controla si el cielo se sincroniza automáticamente con la hora real o si prefieres fijar un ambiente personalizado.',
                'atmosphere',
                <span className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200/80 shrink-0 self-start sm:self-center shadow-2xs">
                  {atmosphereMode === 'auto'
                    ? '🕒 Modo Automático Activo'
                    : atmosphereMode === 'timelapse'
                    ? '⚡ Timelapse 24h Activo'
                    : '🎨 Modo Manual Activo'}
                </span>
              )}
              <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200/80 shadow-xs max-w-3xl space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
                  <div>
                    <h3 className="font-bold text-base sm:text-lg text-slate-900 flex items-center gap-2">
                      <Sun className="w-5 h-5 text-rose-500" />
                      <span>Configuración del Modo de Luz & Clima</span>
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Personaliza cómo los visitantes y la pareja experimentan el cielo y clima del jardín virtual.
                    </p>
                  </div>
                </div>

                {/* MODE SELECTION GRID */}
                <div className="space-y-3">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Selecciona el Modo de Funcionamiento
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Mode 1: Automatic */}
                    <button
                      type="button"
                      onClick={() => setAtmosphereMode('auto')}
                      className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-2 ${
                        atmosphereMode === 'auto'
                          ? 'bg-rose-50/70 border-rose-400 ring-2 ring-rose-200 text-slate-900 shadow-xs'
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100/80 text-slate-700'
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="text-2xl">🕒</span>
                          {atmosphereMode === 'auto' && (
                            <span className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase bg-rose-500 text-white">
                              Recomendado
                            </span>
                          )}
                        </div>
                        <h4 className="font-bold text-sm text-slate-900 mt-2">
                          Automático en Tiempo Real
                        </h4>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                          Sincroniza el sol, luna, estrellas y clima suave en tiempo real según la hora local.
                        </p>
                      </div>
                      <span className="text-[11px] font-semibold text-rose-600 pt-1">
                        Amanecer, Mediodía, Ocaso y Noche reales ✓
                      </span>
                    </button>

                    {/* Mode 2: Manual */}
                    <button
                      type="button"
                      onClick={() => setAtmosphereMode('manual')}
                      className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-2 ${
                        atmosphereMode === 'manual'
                          ? 'bg-rose-50/70 border-rose-400 ring-2 ring-rose-200 text-slate-900 shadow-xs'
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100/80 text-slate-700'
                      }`}
                    >
                      <div>
                        <span className="text-2xl">🎨</span>
                        <h4 className="font-bold text-sm text-slate-900 mt-2">
                          Modo Manual Fijo
                        </h4>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                          Fija una fase celestial específica del día y un clima determinado de forma constante.
                        </p>
                      </div>
                      <span className="text-[11px] font-semibold text-slate-500 pt-1">
                        Fase y clima personalizados a tu gusto
                      </span>
                    </button>

                    {/* Mode 3: Timelapse 24h */}
                    <button
                      type="button"
                      onClick={() => setAtmosphereMode('timelapse')}
                      className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between space-y-2 ${
                        atmosphereMode === 'timelapse'
                          ? 'bg-rose-50/70 border-rose-400 ring-2 ring-rose-200 text-slate-900 shadow-xs'
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100/80 text-slate-700'
                      }`}
                    >
                      <div>
                        <span className="text-2xl">⚡</span>
                        <h4 className="font-bold text-sm text-slate-900 mt-2">
                          Simulación Timelapse
                        </h4>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                          El sol y la luna orbitan continuamente mostrando todo el ciclo día-noche acelerado.
                        </p>
                      </div>
                      <span className="text-[11px] font-semibold text-slate-500 pt-1">
                        Ciclo continuo dinámico
                      </span>
                    </button>
                  </div>
                </div>

                {/* MANUAL SETTINGS PANEL */}
                {atmosphereMode === 'manual' && (
                  <div className="p-4 sm:p-5 rounded-xl bg-slate-50 border border-slate-200 space-y-5 animate-fadeIn">
                    <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                      <Sliders className="w-4 h-4 text-rose-500" />
                      <span>Personalización de Fase del Día & Clima</span>
                    </h3>

                    {/* 1. Time of Day Phase Selector */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">
                        1. Fase Celestial del Día
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setManualTimeOfDay('dawn');
                            setManualMinuteOfDay(345);
                          }}
                          className={`p-3 rounded-xl border text-center transition-all cursor-pointer ${
                            manualTimeOfDay === 'dawn'
                              ? 'bg-white border-rose-400 ring-2 ring-rose-200 text-slate-900 font-bold shadow-xs'
                              : 'bg-white/80 border-slate-200 text-slate-600 hover:bg-white'
                          }`}
                        >
                          <Sunrise className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                          <span className="text-xs block">Amanecer</span>
                          <span className="text-[10px] text-slate-400">05:45 AM</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setManualTimeOfDay('day');
                            setManualMinuteOfDay(720);
                          }}
                          className={`p-3 rounded-xl border text-center transition-all cursor-pointer ${
                            manualTimeOfDay === 'day'
                              ? 'bg-white border-rose-400 ring-2 ring-rose-200 text-slate-900 font-bold shadow-xs'
                              : 'bg-white/80 border-slate-200 text-slate-600 hover:bg-white'
                          }`}
                        >
                          <Sun className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                          <span className="text-xs block">Día Soleado</span>
                          <span className="text-[10px] text-slate-400">12:00 PM</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setManualTimeOfDay('sunset');
                            setManualMinuteOfDay(1065);
                          }}
                          className={`p-3 rounded-xl border text-center transition-all cursor-pointer ${
                            manualTimeOfDay === 'sunset'
                              ? 'bg-white border-rose-400 ring-2 ring-rose-200 text-slate-900 font-bold shadow-xs'
                              : 'bg-white/80 border-slate-200 text-slate-600 hover:bg-white'
                          }`}
                        >
                          <Sunset className="w-5 h-5 mx-auto mb-1 text-orange-500" />
                          <span className="text-xs block">Atardecer</span>
                          <span className="text-[10px] text-slate-400">05:45 PM</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setManualTimeOfDay('night');
                            setManualMinuteOfDay(1320);
                          }}
                          className={`p-3 rounded-xl border text-center transition-all cursor-pointer ${
                            manualTimeOfDay === 'night'
                              ? 'bg-white border-rose-400 ring-2 ring-rose-200 text-slate-900 font-bold shadow-xs'
                              : 'bg-white/80 border-slate-200 text-slate-600 hover:bg-white'
                          }`}
                        >
                          <Moon className="w-5 h-5 mx-auto mb-1 text-indigo-500" />
                          <span className="text-xs block">Noche Mágica</span>
                          <span className="text-[10px] text-slate-400">10:00 PM</span>
                        </button>
                      </div>
                    </div>

                    {/* 2. Weather Selector */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">
                        2. Tipo de Clima
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => setManualWeather('sunny')}
                          className={`p-3 rounded-xl border text-center transition-all cursor-pointer ${
                            manualWeather === 'sunny'
                              ? 'bg-white border-rose-400 ring-2 ring-rose-200 text-slate-900 font-bold shadow-xs'
                              : 'bg-white/80 border-slate-200 text-slate-600 hover:bg-white'
                          }`}
                        >
                          <Sun className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                          <span className="text-xs block">Soleado</span>
                          <span className="text-[10px] text-slate-400">Despejado</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setManualWeather('cloudy')}
                          className={`p-3 rounded-xl border text-center transition-all cursor-pointer ${
                            manualWeather === 'cloudy'
                              ? 'bg-white border-rose-400 ring-2 ring-rose-200 text-slate-900 font-bold shadow-xs'
                              : 'bg-white/80 border-slate-200 text-slate-600 hover:bg-white'
                          }`}
                        >
                          <Cloud className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                          <span className="text-xs block">Nublado</span>
                          <span className="text-[10px] text-slate-400">Nubes suaves</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setManualWeather('rainy')}
                          className={`p-3 rounded-xl border text-center transition-all cursor-pointer ${
                            manualWeather === 'rainy'
                              ? 'bg-white border-rose-400 ring-2 ring-rose-200 text-slate-900 font-bold shadow-xs'
                              : 'bg-white/80 border-slate-200 text-slate-600 hover:bg-white'
                          }`}
                        >
                          <CloudRain className="w-5 h-5 mx-auto mb-1 text-sky-500" />
                          <span className="text-xs block">Lluvia Suave</span>
                          <span className="text-[10px] text-slate-400">Gotas románticas</span>
                        </button>
                      </div>
                    </div>

                    {/* 3. Exact Minute Slider */}
                    <div className="space-y-1.5 pt-2 border-t border-slate-200">
                      <div className="flex items-center justify-between text-xs">
                        <label className="font-semibold text-slate-700">
                          3. Hora Exacta de la Simulación
                        </label>
                        <span className="font-mono text-rose-600 font-bold bg-rose-50 px-2.5 py-0.5 rounded-md border border-rose-100">
                          {Math.floor(manualMinuteOfDay / 60)
                            .toString()
                            .padStart(2, '0')}
                          :
                          {Math.floor(manualMinuteOfDay % 60)
                            .toString()
                            .padStart(2, '0')}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={1439}
                        value={manualMinuteOfDay}
                        onChange={(e) => {
                          const min = Number(e.target.value);
                          setManualMinuteOfDay(min);
                          if (min >= 300 && min < 480) setManualTimeOfDay('dawn');
                          else if (min >= 480 && min < 1020) setManualTimeOfDay('day');
                          else if (min >= 1020 && min < 1200) setManualTimeOfDay('sunset');
                          else setManualTimeOfDay('night');
                        }}
                        className="w-full accent-rose-500 cursor-pointer"
                      />
                      <div className="flex justify-between text-[10px] text-slate-400 font-mono">
                        <span>00:00 (Noche)</span>
                        <span>06:00 (Amanecer)</span>
                        <span>12:00 (Mediodía)</span>
                        <span>18:00 (Atardecer)</span>
                        <span>23:59 (Noche)</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ACTION BUTTONS */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={handleResetToAutoAtmosphere}
                    className="w-full sm:w-auto px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4 text-slate-500" />
                    <span>Restablecer a Modo Automático</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSaveAtmosphere()}
                    className="w-full sm:w-auto px-5 py-2.5 bg-rose-500 hover:bg-rose-600 text-white font-semibold text-xs sm:text-sm rounded-xl shadow-xs transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>Guardar Configuración del Jardín</span>
                  </button>
                </div>
              </div>

            {/* SECCIÓN DE TULIPANES DENTRO DE AMBIENTE */}
            <TulipAdminSection
              settings={settings}
              onSaveSettings={onSaveSettings}
            />

            {renderSectionBottomReturnBar()}
          </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: COLOR DE TULIPANES (/admin/tulipanes) */}
          {/* ======================================================== */}
          {activeSection === 'tulips' && (
            <div className="space-y-4">
              {renderSectionBreadcrumbHeader(
                'Color de los Tulipanes del Jardín 🌷',
                'Personaliza el color de cada uno de los 4 tulipanes por separado o de todas a la vez con paletas románticas o selector libre.',
                'tulips',
                <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-300 border border-pink-500/30 shrink-0 self-start sm:self-center">
                  {settings.tulipSettings?.mode === 'all'
                    ? '✨ Todas Unificadas'
                    : '🎨 Cada una por separado'}
                </span>
              )}
              <TulipAdminSection
                settings={settings}
                onSaveSettings={onSaveSettings}
              />
              <WateringAdminSection
                settings={settings}
                onSaveSettings={onSaveSettings}
              />
              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: RIEGO & CUIDADO DEL JARDÍN (/admin/riego) */}
          {/* ======================================================== */}
          {activeSection === 'watering' && (
            <div className="space-y-4">
              {renderSectionBreadcrumbHeader(
                'Riego & Hidratación del Jardín 💧',
                'Control del nivel de agua de la regadera, recarga diaria, historial y estado de hidratación botánica.',
                'watering',
                <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-sky-50 text-sky-700 border border-sky-200 shrink-0 self-start sm:self-center">
                  💧 Regadera & Flores
                </span>
              )}
              <WateringAdminSection
                settings={settings}
                onSaveSettings={onSaveSettings}
              />
              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: FRASES & DEDICATORIAS (/admin/frases) */}
          {/* ======================================================== */}
          {activeSection === 'phrases' && (
            <div className="space-y-4">
              {renderSectionBreadcrumbHeader(
                'Frases & Dedicatorias Románticas 💬',
                'Personaliza la frase inspiradora que aparece en la portada pública, apodos de cariño y lugar especial.',
                'phrases'
              )}
              <PhrasesAdminSection
                settings={settings}
                onSaveSettings={onSaveSettings}
              />
              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: PAREJA & 2 PERFILES (/admin/pareja) */}
          {/* ======================================================== */}
          {activeSection === 'couple' && (
            <div className="space-y-6">
              {renderSectionBreadcrumbHeader(
                'Perfiles & Autores de la Pareja 👥',
                'Gestión de identidad personal de cada uno (foto en alta definición, nombre, fecha de nacimiento y tipo de sangre) y los datos oficiales de la relación.',
                'couple',
                <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-rose-500/20 text-rose-300 border border-rose-500/30 flex items-center gap-1.5">
                  <Heart className="w-3.5 h-3.5 text-rose-400" />
                  <span>2 Perfiles Activos</span>
                </span>
              )}

              {/* Context & Quick Author Switcher Bar */}
              <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-rose-50 border border-rose-100 flex items-center justify-center text-lg shrink-0 shadow-2xs">
                    👥
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-slate-900 font-bold text-sm sm:text-base">Identidad de Autores & Pareja</h3>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>Cloud Firestore Sync</span>
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Cada recuerdo, carta o publicación muestra quién es el autor con su foto oficial y nombre.
                    </p>
                  </div>
                </div>

                {/* Authoritative Active Session Indicator (No switching) */}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-100 border border-slate-200 self-start md:self-auto select-none">
                  <span className="text-[11px] font-semibold text-slate-500">Sesión autenticada:</span>
                  <span className={`text-xs font-bold flex items-center gap-1.5 ${
                    activeProfile === 'novio' ? 'text-sky-700' : 'text-rose-700'
                  }`}>
                    <span>{activeProfile === 'novio' ? '💙 Novio' : '💗 Novia'}</span>
                  </span>
                </div>
              </div>

              <form onSubmit={handleSaveCouple} className="space-y-6 text-xs sm:text-sm">
                {/* 2 Profiles Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                  {/* ======================================================== */}
                  {/* CARD 1: EL NOVIO */}
                  {/* ======================================================== */}
                  <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="p-4 sm:p-5 border-b border-sky-100 bg-sky-50/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-sky-100 border border-sky-200 text-sky-700 flex items-center justify-center text-lg font-bold shadow-2xs">
                          💙
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-sm sm:text-base text-slate-900">Perfil del Novio</h3>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200">
                              {p1Name || 'El Novio'}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500">Autor de recuerdos, cartas y bitácoras de amor</p>
                        </div>
                      </div>

                      {activeProfile === 'novio' ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-md bg-sky-100 text-sky-700 border border-sky-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse"></span>
                          <span>Tu Sesión Activa</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 border border-slate-200">
                          <span>Cuenta de tu pareja</span>
                        </span>
                      )}
                    </div>

                    <div className="p-4 sm:p-6 space-y-5 flex-1 flex flex-col justify-between">
                      {/* Avatar Management & Preview */}
                      <div className="p-4 rounded-xl bg-slate-50/80 border border-slate-200 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <Camera className="w-3.5 h-3.5 text-sky-600" />
                            <span>Foto de Perfil Oficial</span>
                          </span>
                          {p1Avatar && !imgErrorP1 ? (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              <span>Sincronizada</span>
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                              Avatar predeterminado
                            </span>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4">
                          {/* Avatar Viewport with error-safe preview & hover trigger */}
                          <div className="relative group shrink-0">
                            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-slate-100 border-2 border-sky-300 ring-4 ring-sky-50 shadow-2xs flex items-center justify-center relative">
                              {p1Avatar && !imgErrorP1 ? (
                                <img
                                  src={p1Avatar}
                                  alt={p1Name || 'Novio'}
                                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  onError={() => setImgErrorP1(true)}
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-sky-100 to-slate-200 flex flex-col items-center justify-center text-sky-700">
                                  <span className="text-3xl font-black font-['Playfair_Display',serif]">
                                    {p1Name ? p1Name.trim().charAt(0).toUpperCase() : 'J'}
                                  </span>
                                  <span className="text-[10px] text-sky-600 font-semibold mt-0.5">El Novio</span>
                                </div>
                              )}

                              {/* Saving indicator spinner */}
                              {isSavingAvatar1 && (
                                <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center gap-1 z-20">
                                  <RefreshCw className="w-6 h-6 text-sky-600 animate-spin" />
                                  <span className="text-[9px] font-bold text-sky-700">Guardando...</span>
                                </div>
                              )}

                              {/* Hover overlay */}
                              {!isSavingAvatar1 && (
                                <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white cursor-pointer z-10">
                                  <Camera className="w-6 h-6 text-white drop-shadow" />
                                  <span className="text-[10px] font-bold mt-1">Cambiar</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    disabled={isSavingAvatar1}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleUploadAvatar('novio', file);
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                          </div>

                          {/* Controls Column */}
                          <div className="min-w-0 flex-1 space-y-2 text-center sm:text-left">
                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                              {/* Direct File Upload button */}
                              <label className="px-3.5 py-1.5 rounded-xl bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 text-xs font-bold cursor-pointer transition-all flex items-center gap-1.5 shadow-2xs">
                                <Upload className="w-3.5 h-3.5 text-sky-600" />
                                <span>{isSavingAvatar1 ? 'Guardando...' : p1Avatar ? 'Actualizar Foto' : 'Subir Foto'}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  disabled={isSavingAvatar1}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleUploadAvatar('novio', file);
                                  }}
                                />
                              </label>

                              {/* URL toggle button */}
                              <button
                                type="button"
                                onClick={() => setShowUrlInput1(!showUrlInput1)}
                                className="px-3 py-1.5 rounded-xl bg-white hover:bg-slate-50 text-slate-700 text-xs border border-slate-200 transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs"
                              >
                                <Globe className="w-3 h-3 text-slate-400" />
                                <span>Por URL</span>
                              </button>

                              {/* Remove button */}
                              {p1Avatar && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveAvatar('novio')}
                                  className="px-2.5 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs border border-rose-200 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                  <Trash2 className="w-3 h-3 text-rose-500" />
                                  <span>Quitar</span>
                                </button>
                              )}
                            </div>

                            <p className="text-[11px] text-slate-500 leading-relaxed">
                              La imagen se comprime automáticamente a formato web de alto rendimiento para garantizar guardado perpetuo sin pérdida.
                            </p>

                            {/* Collapsible URL Input */}
                            {showUrlInput1 && (
                              <div className="p-2.5 rounded-xl bg-white border border-slate-200 space-y-2 mt-2 shadow-xs">
                                <label className="text-[10px] font-bold text-slate-700 block">
                                  Ingresa el enlace directo de la imagen (HTTPS):
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="url"
                                    placeholder="https://servidor.com/foto.jpg"
                                    value={urlInput1}
                                    onChange={(e) => setUrlInput1(e.target.value)}
                                    className="flex-1 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 text-xs focus:outline-none focus:ring-1 focus:ring-sky-400"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleSaveAvatarFromUrl('novio', urlInput1)}
                                    className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs rounded-lg transition-all cursor-pointer"
                                  >
                                    Guardar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setShowUrlInput1(false)}
                                    className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs rounded-lg cursor-pointer"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Personal Details Form Fields */}
                      <div className="space-y-4">
                        {/* Field 1: Nombre */}
                        <div>
                          <label className="font-bold text-slate-700 mb-1.5 text-xs flex items-center justify-between">
                            <span className="flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-sky-600" />
                              <span>Nombre del Novio</span>
                              <span className="text-rose-500">*</span>
                            </span>
                            <span className="text-[10px] text-slate-400 font-normal">Firma pública</span>
                          </label>
                          <input
                            type="text"
                            required
                            placeholder="Ej. Nombre del novio..."
                            value={p1Name}
                            onChange={(e) => setP1Name(e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm placeholder:text-slate-400"
                          />
                        </div>

                        {/* Field 2: Título o Apodo */}
                        <div>
                          <label className="font-bold text-slate-700 mb-1.5 text-xs flex items-center justify-between">
                            <span className="flex items-center gap-1.5">
                              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                              <span>Título o Apodo Romántico</span>
                            </span>
                            <span className="text-[10px] text-slate-400 font-normal">Encabezados y dedicatorias</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Ej. El Novio, Mi Amor, Mi Rey..."
                            value={p1Title}
                            onChange={(e) => setP1Title(e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-xs placeholder:text-slate-400"
                          />
                        </div>

                        {/* Field 3: Fecha de Nacimiento */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="font-bold text-slate-700 text-xs flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-purple-600" />
                              <span>Fecha de Nacimiento</span>
                            </label>
                            {(() => {
                              const bInfo = getProfileBirthdayInfo(p1BirthDate);
                              if (!bInfo) return null;
                              return (
                                <span className="text-[11px] font-bold text-sky-700 flex items-center gap-1">
                                  <Cake className="w-3 h-3 text-amber-500" />
                                  <span>{bInfo.age} años</span>
                                </span>
                              );
                            })()}
                          </div>
                          <input
                            type="date"
                            value={p1BirthDate}
                            onChange={(e) => setP1BirthDate(e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-400 text-xs font-mono"
                          />

                          {/* Dynamic Birthday Badge */}
                          {(() => {
                            const bInfo = getProfileBirthdayInfo(p1BirthDate);
                            if (bInfo) {
                              return (
                                <div className="mt-2 p-2 rounded-lg bg-sky-50 border border-sky-100 text-[11px] text-sky-800 flex items-center justify-between">
                                  <span className="flex items-center gap-1.5">
                                    <Cake className="w-3.5 h-3.5 text-amber-500" />
                                    <span>Cumpleaños: <strong>{bInfo.formattedBirthday}</strong></span>
                                  </span>
                                  <span className="font-bold px-2 py-0.5 rounded bg-sky-100 text-sky-700">
                                    {bInfo.countdown.isToday
                                      ? '🎉 ¡Hoy es su cumpleaños! 🎂'
                                      : `Faltan ${bInfo.countdown.daysRemaining} días`}
                                  </span>
                                </div>
                              );
                            }
                            return (
                              <p className="text-[10px] text-slate-400 mt-1">
                                Ingresa la fecha para calcular su edad actual y cuenta regresiva de cumpleaños.
                              </p>
                            );
                          })()}
                        </div>

                        {/* Field 4: Tipo de Sangre */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="font-bold text-slate-700 text-xs flex items-center gap-1.5">
                              <Droplets className="w-3.5 h-3.5 text-rose-500" />
                              <span>Tipo de Sangre</span>
                            </label>
                            <span className="text-[11px] font-black px-2.5 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200">
                              {p1BloodType || 'O+'}
                            </span>
                          </div>
                          <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                            {['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'].map((type) => {
                              const isSelected = (p1BloodType || 'O+') === type;
                              return (
                                <button
                                  key={type}
                                  type="button"
                                  onClick={() => setP1BloodType(type)}
                                  className={`py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                                    isSelected
                                      ? 'bg-sky-500 text-white shadow-2xs scale-105'
                                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'
                                  }`}
                                >
                                  {type}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Footer Protection Note */}
                      <div className="pt-2">
                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-[11px] text-slate-600 flex items-center gap-2">
                          <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                          <span>Foto y perfil protegidos en Firestore y almacenamiento local.</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ======================================================== */}
                  {/* CARD 2: LA NOVIA */}
                  {/* ======================================================== */}
                  <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="p-4 sm:p-5 border-b border-rose-100 bg-rose-50/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-rose-100 border border-rose-200 text-rose-700 flex items-center justify-center text-lg font-bold shadow-2xs">
                          💖
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-sm sm:text-base text-slate-900">Perfil de la Novia</h3>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200">
                              {p2Name || 'La Novia'}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500">Autora de recuerdos, cartas y bitácoras de amor</p>
                        </div>
                      </div>

                      {activeProfile === 'novia' ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-md bg-rose-100 text-rose-700 border border-rose-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
                          <span>Tu Sesión Activa</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 border border-slate-200">
                          <span>Cuenta de tu pareja</span>
                        </span>
                      )}
                    </div>

                    <div className="p-4 sm:p-6 space-y-5 flex-1 flex flex-col justify-between">
                      {/* Avatar Management & Preview */}
                      <div className="p-4 rounded-xl bg-slate-50/80 border border-slate-200 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <Camera className="w-3.5 h-3.5 text-rose-600" />
                            <span>Foto de Perfil Oficial</span>
                          </span>
                          {p2Avatar && !imgErrorP2 ? (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              <span>Sincronizada</span>
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                              Avatar predeterminado
                            </span>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4">
                          {/* Avatar Viewport with error-safe preview & hover trigger */}
                          <div className="relative group shrink-0">
                            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-slate-100 border-2 border-rose-300 ring-4 ring-rose-50 shadow-2xs flex items-center justify-center relative">
                              {p2Avatar && !imgErrorP2 ? (
                                <img
                                  src={p2Avatar}
                                  alt={p2Name || 'Novia'}
                                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  onError={() => setImgErrorP2(true)}
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-rose-100 to-slate-200 flex flex-col items-center justify-center text-rose-700">
                                  <span className="text-3xl font-black font-['Playfair_Display',serif]">
                                    {p2Name ? p2Name.trim().charAt(0).toUpperCase() : 'N'}
                                  </span>
                                  <span className="text-[10px] text-rose-600 font-semibold mt-0.5">La Novia</span>
                                </div>
                              )}

                              {/* Saving indicator spinner */}
                              {isSavingAvatar2 && (
                                <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center gap-1 z-20">
                                  <RefreshCw className="w-6 h-6 text-rose-600 animate-spin" />
                                  <span className="text-[9px] font-bold text-rose-700">Guardando...</span>
                                </div>
                              )}

                              {/* Hover overlay */}
                              {!isSavingAvatar2 && (
                                <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white cursor-pointer z-10">
                                  <Camera className="w-6 h-6 text-white drop-shadow" />
                                  <span className="text-[10px] font-bold mt-1">Cambiar</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    disabled={isSavingAvatar2}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleUploadAvatar('novia', file);
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                          </div>

                          {/* Controls Column */}
                          <div className="min-w-0 flex-1 space-y-2 text-center sm:text-left">
                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                              {/* Direct File Upload button */}
                              <label className="px-3.5 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-bold cursor-pointer transition-all flex items-center gap-1.5 shadow-2xs">
                                <Upload className="w-3.5 h-3.5 text-rose-600" />
                                <span>{isSavingAvatar2 ? 'Guardando...' : p2Avatar ? 'Actualizar Foto' : 'Subir Foto'}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  disabled={isSavingAvatar2}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleUploadAvatar('novia', file);
                                  }}
                                />
                              </label>

                              {/* URL toggle button */}
                              <button
                                type="button"
                                onClick={() => setShowUrlInput2(!showUrlInput2)}
                                className="px-3 py-1.5 rounded-xl bg-white hover:bg-slate-50 text-slate-700 text-xs border border-slate-200 transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs"
                              >
                                <Globe className="w-3 h-3 text-slate-400" />
                                <span>Por URL</span>
                              </button>

                              {/* Remove button */}
                              {p2Avatar && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveAvatar('novia')}
                                  className="px-2.5 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs border border-rose-200 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                  <Trash2 className="w-3 h-3 text-rose-500" />
                                  <span>Quitar</span>
                                </button>
                              )}
                            </div>

                            <p className="text-[11px] text-slate-500 leading-relaxed">
                              La imagen se comprime automáticamente a formato web de alto rendimiento para garantizar guardado perpetuo sin pérdida.
                            </p>

                            {/* Collapsible URL Input */}
                            {showUrlInput2 && (
                              <div className="p-2.5 rounded-xl bg-white border border-slate-200 space-y-2 mt-2 shadow-xs">
                                <label className="text-[10px] font-bold text-slate-700 block">
                                  Ingresa el enlace directo de la imagen (HTTPS):
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="url"
                                    placeholder="https://servidor.com/foto.jpg"
                                    value={urlInput2}
                                    onChange={(e) => setUrlInput2(e.target.value)}
                                    className="flex-1 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 text-xs focus:outline-none focus:ring-1 focus:ring-rose-400"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleSaveAvatarFromUrl('novia', urlInput2)}
                                    className="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-lg transition-all cursor-pointer"
                                  >
                                    Guardar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setShowUrlInput2(false)}
                                    className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs rounded-lg cursor-pointer"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Personal Details Form Fields */}
                      <div className="space-y-4">
                        {/* Field 1: Nombre */}
                        <div>
                          <label className="font-bold text-slate-700 mb-1.5 text-xs flex items-center justify-between">
                            <span className="flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-rose-600" />
                              <span>Nombre de la Novia</span>
                              <span className="text-rose-500">*</span>
                            </span>
                            <span className="text-[10px] text-slate-400 font-normal">Firma pública</span>
                          </label>
                          <input
                            type="text"
                            required
                            placeholder="Ej. Nombre de la novia..."
                            value={p2Name}
                            onChange={(e) => setP2Name(e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm placeholder:text-slate-400"
                          />
                        </div>

                        {/* Field 2: Título o Apodo */}
                        <div>
                          <label className="font-bold text-slate-700 mb-1.5 text-xs flex items-center justify-between">
                            <span className="flex items-center gap-1.5">
                              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                              <span>Título o Apodo Romántico</span>
                            </span>
                            <span className="text-[10px] text-slate-400 font-normal">Encabezados y dedicatorias</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Ej. La Novia, Mi Princesa, Mi Niña..."
                            value={p2Title}
                            onChange={(e) => setP2Title(e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-400 text-xs placeholder:text-slate-400"
                          />
                        </div>

                        {/* Field 3: Fecha de Nacimiento */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="font-bold text-slate-700 text-xs flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-purple-600" />
                              <span>Fecha de Nacimiento</span>
                            </label>
                            {(() => {
                              const bInfo = getProfileBirthdayInfo(p2BirthDate);
                              if (!bInfo) return null;
                              return (
                                <span className="text-[11px] font-bold text-rose-700 flex items-center gap-1">
                                  <Cake className="w-3 h-3 text-amber-500" />
                                  <span>{bInfo.age} años</span>
                                </span>
                              );
                            })()}
                          </div>
                          <input
                            type="date"
                            value={p2BirthDate}
                            onChange={(e) => setP2BirthDate(e.target.value)}
                            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-400 text-xs font-mono"
                          />

                          {/* Dynamic Birthday Badge */}
                          {(() => {
                            const bInfo = getProfileBirthdayInfo(p2BirthDate);
                            if (bInfo) {
                              return (
                                <div className="mt-2 p-2 rounded-lg bg-rose-50 border border-rose-100 text-[11px] text-rose-800 flex items-center justify-between">
                                  <span className="flex items-center gap-1.5">
                                    <Cake className="w-3.5 h-3.5 text-amber-500" />
                                    <span>Cumpleaños: <strong>{bInfo.formattedBirthday}</strong></span>
                                  </span>
                                  <span className="font-bold px-2 py-0.5 rounded bg-rose-100 text-rose-700">
                                    {bInfo.countdown.isToday
                                      ? '🎉 ¡Hoy es su cumpleaños! 🎂'
                                      : `Faltan ${bInfo.countdown.daysRemaining} días`}
                                  </span>
                                </div>
                              );
                            }
                            return (
                              <p className="text-[10px] text-slate-400 mt-1">
                                Ingresa la fecha para calcular su edad actual y cuenta regresiva de cumpleaños.
                              </p>
                            );
                          })()}
                        </div>

                        {/* Field 4: Tipo de Sangre */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="font-bold text-slate-700 text-xs flex items-center gap-1.5">
                              <Droplets className="w-3.5 h-3.5 text-rose-500" />
                              <span>Tipo de Sangre</span>
                            </label>
                            <span className="text-[11px] font-black px-2.5 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200">
                              {p2BloodType || 'O+'}
                            </span>
                          </div>
                          <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                            {['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'].map((type) => {
                              const isSelected = (p2BloodType || 'O+') === type;
                              return (
                                <button
                                  key={type}
                                  type="button"
                                  onClick={() => setP2BloodType(type)}
                                  className={`py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                                    isSelected
                                      ? 'bg-rose-500 text-white shadow-2xs scale-105'
                                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'
                                  }`}
                                >
                                  {type}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Footer Protection Note */}
                      <div className="pt-2">
                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-[11px] text-slate-600 flex items-center gap-2">
                          <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                          <span>Foto y perfil protegidos en Firestore y almacenamiento local.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ======================================================== */}
                {/* CARD 3: DATOS COMPARTIDOS DE LA PAREJA */}
                {/* ======================================================== */}
                <div className="p-5 sm:p-6 rounded-2xl bg-white border border-slate-200/80 shadow-xs space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center text-lg font-bold shadow-2xs">
                      💑
                    </div>
                    <div>
                      <h3 className="font-bold text-sm sm:text-base text-slate-900 flex items-center gap-2">
                        <span>Datos Compartidos de la Pareja</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                          Nuestra Historia
                        </span>
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Fecha de aniversario oficial de la relación y frase o lema que encabeza el libro de amor.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                    {/* Aniversario */}
                    <div className="space-y-1.5">
                      <label className="block font-bold text-slate-700 text-xs">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-amber-500" />
                          <span>Fecha de Inicio de la Relación (Aniversario)</span>
                          <span className="text-rose-500">*</span>
                        </span>
                      </label>
                      <input
                        type="date"
                        required
                        value={annivDate}
                        onChange={(e) => setAnnivDate(e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400 font-mono text-sm"
                      />
                      <div className="p-2.5 rounded-xl bg-amber-50/70 border border-amber-200 text-xs text-amber-900 flex items-center gap-2">
                        <Heart className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                        <span>
                          Tiempo juntos acumulado: <strong>{currentTime.years} Años, {currentTime.months} Meses, {currentTime.days} Días</strong>.
                        </span>
                      </div>
                    </div>

                    {/* Lema */}
                    <div className="space-y-1.5">
                      <label className="block font-bold text-slate-700 text-xs">
                        <span className="flex items-center gap-1.5">
                          <MessageSquare className="w-3.5 h-3.5 text-pink-500" />
                          <span>Frase o Lema Romántico de la Pareja</span>
                        </span>
                      </label>
                      <input
                        type="text"
                        value={romanticQuote}
                        onChange={(e) => setRomanticQuote(e.target.value)}
                        placeholder="Ej. Cada segundo a tu lado es un regalo 🧡"
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
                      />
                      <div className="p-2 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-600 italic truncate">
                        Vista previa: “{romanticQuote || 'Cada segundo a tu lado es un regalo 🧡'}”
                      </div>
                    </div>
                  </div>
                </div>

                {/* ======================================================== */}
                {/* BOTTOM ACTION BAR */}
                {/* ======================================================== */}
                <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5 text-xs text-slate-600">
                    <ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0" />
                    <span>
                      Todos los datos y fotos quedan respaldados permanentemente en <strong>Firebase Firestore</strong> y en tu navegador.
                    </span>
                  </div>

                  <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                    <button
                      type="button"
                      onClick={handleResetCoupleForm}
                      className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all border border-slate-200 flex items-center gap-1.5 cursor-pointer"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      <span>Descartar</span>
                    </button>

                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer hover:scale-[1.01] active:scale-[0.99] text-xs sm:text-sm"
                    >
                      <Save className="w-4 h-4 text-rose-400" />
                      <span>Guardar Todos los Perfiles & Pareja</span>
                    </button>
                  </div>
                </div>
              </form>
              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: CLAVE & SEGURIDAD (/admin/seguridad) */}
          {/* ======================================================== */}
          {activeSection === 'security' && (
            <div className="space-y-6 max-w-4xl">
              {renderSectionBreadcrumbHeader(
                'Seguridad & Cuentas Administrativas 🔐',
                'Crea y gestiona cuentas de acceso para el novio o para la novia solicitando únicamente su correo y contraseña.',
                'security'
              )}

              {/* Current Active Session Profile Banner */}
              <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-4 sm:p-5 rounded-2xl shadow-sm border border-slate-700/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-11 h-11 rounded-2xl flex items-center justify-center text-lg font-bold shadow-inner ${
                      currentEffectiveProfile === 'novio'
                        ? 'bg-blue-600/30 text-blue-300 border border-blue-500/40'
                        : 'bg-rose-600/30 text-rose-300 border border-rose-500/40'
                    }`}
                  >
                    {currentEffectiveProfile === 'novio' ? '💙' : '💖'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Sesión Actual Activa
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                        Sesión Segura
                      </span>
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                      <span>
                        {currentEffectiveProfile === 'novio'
                          ? settings.partner1Name || 'Novio'
                          : settings.partner2Name || 'Novia'}
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-lg bg-white/10 text-slate-200">
                        {currentEffectiveProfile === 'novio' ? 'Perfil Novio' : 'Perfil Novia'}
                      </span>
                    </h3>
                  </div>
                </div>
              </div>

              {/* ======================================================== */}
              {/* APARTADO: CREAR CUENTA ADMINISTRATIVA (NOVIO O NOVIA) */}
              {/* Pide solamente Correo y Contraseña */}
              {/* ======================================================== */}
              <div className="bg-white p-5 sm:p-7 rounded-2xl border border-rose-200/80 shadow-xs relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-rose-500 to-amber-500" />

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-rose-500 to-amber-500 text-white flex items-center justify-center font-bold shadow-md shadow-rose-500/20">
                      <KeyRound className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-base sm:text-lg text-slate-900 leading-tight">
                        Crear Cuenta Administrativa
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Registra una cuenta de acceso para la novia o el novio pidiendo solamente correo y contraseña.
                      </p>
                    </div>
                  </div>
                  <span className="self-start sm:self-auto px-3 py-1 rounded-full text-[11px] font-bold bg-slate-900 text-white flex items-center gap-1.5 shadow-xs">
                    <ShieldCheck className="w-3.5 h-3.5 text-rose-400" />
                    <span>Acceso Protegido</span>
                  </span>
                </div>

                <form onSubmit={handleCreateAdminAccount} className="space-y-4 max-w-xl">
                  {/* Selector: ¿Para quién es la cuenta? */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                      1. ¿Para quién es esta cuenta?
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setCreateRole('novia');
                          setCreateError('');
                        }}
                        className={`p-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                          createRole === 'novia'
                            ? 'bg-rose-50 border-rose-400 text-rose-800 ring-2 ring-rose-200/80 shadow-xs'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span className="text-base">💖</span>
                        <span>Para la Novia ({settings.partner2Name || 'Ella'})</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setCreateRole('novio');
                          setCreateError('');
                        }}
                        className={`p-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                          createRole === 'novio'
                            ? 'bg-blue-50 border-blue-400 text-blue-800 ring-2 ring-blue-200/80 shadow-xs'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span className="text-base">💙</span>
                        <span>Para el Novio ({settings.partner1Name || 'Él'})</span>
                      </button>
                    </div>
                  </div>

                  {/* Campo: Correo Electrónico */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      2. Correo Electrónico
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="email"
                        required
                        value={createEmail}
                        onChange={(e) => {
                          setCreateEmail(e.target.value);
                          if (createError) setCreateError('');
                        }}
                        placeholder={createRole === 'novia' ? 'ej. correo-novia@gmail.com' : 'ej. correo-novio@gmail.com'}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100 text-xs sm:text-sm font-medium"
                      />
                    </div>
                  </div>

                  {/* Campo: Contraseña */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                        3. Contraseña
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowCreatePass(!showCreatePass)}
                        className="text-[11px] text-rose-600 hover:text-rose-700 font-medium cursor-pointer"
                      >
                        {showCreatePass ? 'Ocultar' : 'Mostrar'}
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type={showCreatePass ? 'text' : 'password'}
                        required
                        value={createPassword}
                        onChange={(e) => {
                          setCreatePassword(e.target.value);
                          if (createError) setCreateError('');
                        }}
                        placeholder="Mínimo 6 caracteres..."
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100 font-mono text-xs sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Campo: Confirmar Contraseña */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      4. Confirmar Contraseña
                    </label>
                    <div className="relative">
                      <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type={showCreatePass ? 'text' : 'password'}
                        required
                        value={createConfirmPassword}
                        onChange={(e) => {
                          setCreateConfirmPassword(e.target.value);
                          if (createError) setCreateError('');
                        }}
                        placeholder="Repite la contraseña..."
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100 font-mono text-xs sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Feedback Messages */}
                  {createError && (
                    <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs font-medium flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0 text-rose-500" />
                      <span>{createError}</span>
                    </div>
                  )}

                  {createSuccess && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-medium flex items-center gap-2">
                      <Check className="w-4 h-4 shrink-0 text-emerald-600" />
                      <span>{createSuccess}</span>
                    </div>
                  )}

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={createLoading}
                      className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:from-rose-600 hover:to-amber-600 disabled:opacity-60 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md shadow-rose-500/20 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <Plus className="w-4 h-4" />
                      <span>
                        {createLoading
                          ? 'Creando cuenta en Firebase...'
                          : `Crear Cuenta de ${createRole === 'novia' ? 'la Novia 💖' : 'el Novio 💙'}`}
                      </span>
                    </button>
                  </div>
                </form>
              </div>

              {/* ======================================================== */}
              {/* RESUMEN DE CUENTAS ADMINISTRATIVAS VINCULADAS */}
              {/* ======================================================== */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Novio Summary */}
                <div className="bg-white p-5 rounded-2xl border border-blue-200/80 shadow-xs relative overflow-hidden">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 font-bold flex items-center justify-center border border-blue-100">
                        💙
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-slate-900">Cuenta de Novio</h4>
                        <p className="text-[11px] text-slate-500">{settings.partner1Name || 'Novio'}</p>
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                      Perfil Novio 💙
                    </span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/70">
                      <span className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Correo Registrado</span>
                      {effectiveNovioEmail ? (
                        <div className="space-y-1">
                          <span className="font-mono text-xs font-semibold text-slate-900 truncate block">
                            {effectiveNovioEmail}
                          </span>
                          {effectiveNovioUid && (
                            <span className="text-[10px] font-mono text-slate-400 block truncate">
                              UID: {effectiveNovioUid}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 mt-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            Cuenta Vinculada & Activa
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-amber-600 font-medium italic block">
                          Sin cuenta asignada todavía. Créala en el formulario de arriba.
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500">
                      {effectiveNovioEmail
                        ? 'Al iniciar sesión con este correo, entra directamente al perfil de Novio 💙.'
                        : 'Cuando crees su cuenta, él podrá ingresar directamente a su perfil de Novio 💙.'}
                    </p>
                  </div>
                </div>

                {/* Novia Summary */}
                <div className="bg-white p-5 rounded-2xl border border-rose-200/80 shadow-xs relative overflow-hidden">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-xl bg-rose-50 text-rose-600 font-bold flex items-center justify-center border border-rose-100">
                        💖
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-slate-900">Cuenta de Novia</h4>
                        <p className="text-[11px] text-slate-500">{settings.partner2Name || 'Novia'}</p>
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                      Perfil Novia 💖
                    </span>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/70">
                      <span className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Correo Registrado</span>
                      {effectiveNoviaEmail ? (
                        <div className="space-y-1">
                          <span className="font-mono text-xs font-semibold text-slate-900 truncate block">
                            {effectiveNoviaEmail}
                          </span>
                          {effectiveNoviaUid && (
                            <span className="text-[10px] font-mono text-slate-400 block truncate">
                              UID: {effectiveNoviaUid}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 mt-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                            Cuenta Vinculada & Activa
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-amber-600 font-medium italic block">
                          Sin cuenta asignada todavía. Créala en el formulario de arriba.
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500">
                      {effectiveNoviaEmail
                        ? 'Al iniciar sesión con su correo, entra directamente a su perfil de Novia 💖.'
                        : 'Cuando crees su cuenta, ella podrá ingresar directamente a su perfil de Novia 💖.'}
                    </p>
                  </div>
                </div>
              </div>

              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: RESPALDO & BASE DE DATOS (/admin/basededatos) */}
          {/* ======================================================== */}
          {activeSection === 'database' && (
            <div className="space-y-4 max-w-2xl">
              {renderSectionBreadcrumbHeader(
                'Base de Datos en Tiempo Real & Respaldos 💾',
                'Estado de la sincronización en la nube con Google Cloud Firestore y opciones de copia de seguridad en formato JSON.',
                'database'
              )}

              {/* Firestore Real-time Connection Status */}
              <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200/80 shadow-xs">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">
                      Base de Datos en Tiempo Real Conectada
                    </h2>
                  </div>
                  <span className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Firestore ACTIVO
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Todas las fotos, videos, cartas de amor y configuraciones se sincronizan en tiempo real directamente en la nube. Cualquier cambio que hagas aquí se refleja al instante en todos los dispositivos.
                </p>

                {/* Real-time stats */}
                <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100 text-center">
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <span className="block text-xl font-bold text-slate-900">{memories.length}</span>
                    <span className="text-[11px] text-slate-500 font-medium">Recuerdos Reales</span>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <span className="block text-xl font-bold text-slate-900">{lettersList.length}</span>
                    <span className="text-[11px] text-slate-500 font-medium">Cartas Registradas</span>
                  </div>
                </div>
              </div>

              {/* Backups */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col justify-between">
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2 mb-1">
                      <Download className="w-4 h-4 text-rose-500" />
                      <span>Exportar Respaldo Completo</span>
                    </h3>
                    <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                      Descarga un archivo JSON con los {memories.length} recuerdos, {lettersList.length} cartas y configuraciones.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleExportBackup}
                    className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs rounded-xl shadow-xs cursor-pointer flex items-center justify-center gap-2 transition-all active:scale-95"
                  >
                    <Download className="w-3.5 h-3.5 text-rose-400" />
                    <span>Descargar archivo .JSON</span>
                  </button>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col justify-between">
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2 mb-1">
                      <Upload className="w-4 h-4 text-indigo-500" />
                      <span>Restaurar Copia de Seguridad</span>
                    </h3>
                    <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                      Selecciona un archivo JSON previamente guardado para restaurar recuerdos, cartas y fechas.
                    </p>
                  </div>

                  <label className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-xs rounded-xl shadow-xs cursor-pointer flex items-center justify-center gap-2 text-center border border-slate-200 transition-all active:scale-95">
                    <Upload className="w-3.5 h-3.5 text-slate-500" />
                    <span>Seleccionar archivo .JSON</span>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportFile}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Clean test data / Purge button */}
              <div className="bg-white p-5 rounded-2xl border border-rose-200 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3">
                <div>
                  <h4 className="font-bold text-xs text-rose-700 flex items-center gap-1.5 mb-0.5">
                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                    <span>Limpiar Datos de Muestra & Iniciar en Blanco</span>
                  </h4>
                  <p className="text-xs text-slate-500">
                    Elimina cualquier recuerdo o carta de demostración y deja la base de datos limpia para que solo contenga sus historias reales.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmDialog({
                      isOpen: true,
                      title: '¿Limpiar datos de muestra?',
                      description: '¿Confirmas limpiar todos los datos de demostración de la base de datos para comenzar desde cero con información 100% real de ambos?',
                      type: 'danger',
                      confirmText: 'Sí, limpiar datos',
                      onConfirm: () => {
                        onResetDefaultData();
                        triggerToast('Base de datos limpiada con éxito. Listo para información real.');
                        setConfirmDialog(null);
                      },
                    });
                  }}
                  className="px-4 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-semibold text-xs rounded-xl transition-colors cursor-pointer shrink-0 border border-rose-200 flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Limpiar y Empezar en Blanco</span>
                </button>
              </div>
              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: NAVEGACIÓN & INTERFACES (/admin/navegacion) */}
          {/* ======================================================== */}
          {activeSection === 'navigation' && (
            <div className="space-y-6 animate-fadeIn" id="admin-section-navigation">
              {renderSectionBreadcrumbHeader(
                'Visibilidad de Interfaces Públicas',
                'Activa u oculta cada opción en la barra de navegación superior y dock móvil en tiempo real.',
                'navigation',
                <button
                  type="button"
                  onClick={() => onNavigateToPublic('garden')}
                  className="px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/15 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-amber-300" />
                  <span>Probar Web Pública</span>
                </button>
              )}

              {/* Feedback toast banner if changes were saved */}
              {navFeedback && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className={`p-3.5 rounded-2xl border flex items-center gap-3 text-xs font-semibold ${
                    navFeedback.type === 'success'
                      ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200'
                      : 'bg-amber-500/15 border-amber-500/30 text-amber-200'
                  }`}
                >
                  {navFeedback.type === 'success' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                  )}
                  <span>{navFeedback.text}</span>
                </motion.div>
              )}

              {/* Top Overview & Quick Presets */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-rose-50 text-rose-600 border border-rose-100 flex items-center justify-center font-bold shadow-2xs shrink-0">
                      <Compass className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                        <span>Menú y Navegación Pública</span>
                        <span className="px-2.5 py-0.5 rounded-md text-[10px] font-mono font-bold bg-rose-50 text-rose-700 border border-rose-200">
                          {isUserWeb
                            ? `${Object.entries(navVisibility).filter(([k, v]) => k !== 'myWeb' && k !== 'myweb' && Boolean(v)).length} de 4 activas`
                            : `${Object.values(navVisibility).filter(Boolean).length} de 5 activas`}
                        </span>
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Define qué pestañas pueden ver y acceder los visitantes o tu pareja en la cabecera y dock inferior.
                      </p>
                    </div>
                  </div>

                  {/* Preset quick actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleSetAllNavVisible(true)}
                      className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs"
                      title="Activar las 5 interfaces"
                    >
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      <span>Mostrar Todas</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSetAllNavVisible(false)}
                      className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5"
                      title="Dejar visible solo el jardín"
                    >
                      <span>Solo Jardín</span>
                    </button>
                  </div>
                </div>

                {/* Live Navigation Mockup Preview */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 space-y-2.5">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span className="flex items-center gap-1.5">
                      <Eye className="w-3.5 h-3.5 text-rose-500" />
                      <span>Vista previa en vivo de la barra pública (así la verán los visitantes):</span>
                    </span>
                    <span className="text-emerald-600 font-mono text-[10px] font-semibold">Sincronización instantánea</span>
                  </div>

                  {/* Desktop Preview Bar */}
                  <div className="p-3 rounded-xl bg-white border border-slate-200 flex items-center justify-center overflow-x-auto shadow-2xs">
                    <div className="inline-flex items-center p-1 rounded-full bg-slate-100 border border-slate-200 gap-1">
                      {navVisibility.garden && (
                        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white text-slate-900 shadow-2xs">
                          <Flower2 className="w-3.5 h-3.5 text-rose-500" />
                          <span>Jardín</span>
                        </div>
                      )}
                      {navVisibility.blog && (
                        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-slate-600">
                          <Camera className="w-3.5 h-3.5 text-pink-500" />
                          <span>Galería</span>
                        </div>
                      )}
                      {navVisibility.letters && (
                        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-slate-600">
                          <Mail className="w-3.5 h-3.5 text-amber-500" />
                          <span>Cartas Diarias</span>
                        </div>
                      )}
                      {navVisibility.animations && (
                        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-slate-600">
                          <Quote className="w-3.5 h-3.5 text-purple-500 rotate-180" />
                          <span>Frases</span>
                        </div>
                      )}
                      {!isUserWeb && navVisibility.myWeb && (
                        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-slate-600">
                          <Globe className="w-3.5 h-3.5 text-sky-500" />
                          <span>Mi web</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 5 Interface Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 1. Jardín Interactivo */}
                <div
                  className={`p-5 rounded-2xl border transition-all ${
                    navVisibility.garden
                      ? 'bg-white border-slate-300 ring-1 ring-rose-200/50 shadow-xs'
                      : 'bg-slate-50/70 border-slate-200 opacity-75'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shrink-0 ${
                          navVisibility.garden
                            ? 'bg-rose-50 text-rose-600 border border-rose-100'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        <Flower2 className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-sm text-slate-900">Jardín Interactivo</h4>
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                              navVisibility.garden
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}
                          >
                            {navVisibility.garden ? 'Visible' : 'Oculto'}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-400 block mt-0.5">
                          Rutas: / y /jardin
                        </span>
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <button
                      type="button"
                      onClick={() => handleToggleNavVisibility('garden')}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        navVisibility.garden ? 'bg-rose-500' : 'bg-slate-300'
                      }`}
                      title={navVisibility.garden ? 'Desactivar / Ocultar' : 'Activar / Mostrar'}
                      aria-pressed={navVisibility.garden}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${
                          navVisibility.garden ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                    Cielo dinámico con ciclo solar, flores interactivas de amor, nubes, mariposas y contador en vivo de aniversario.
                  </p>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                    <span className="text-slate-400 font-medium">Estado en la barra pública:</span>
                    <button
                      type="button"
                      onClick={() => handleToggleNavVisibility('garden')}
                      className={`font-bold transition-colors cursor-pointer ${
                        navVisibility.garden
                          ? 'text-rose-600 hover:text-rose-700'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {navVisibility.garden ? 'Ocultar interfaz' : 'Activar interfaz'}
                    </button>
                  </div>
                </div>

                {/* 2. Galería de Recuerdos */}
                <div
                  className={`p-5 rounded-2xl border transition-all ${
                    navVisibility.blog
                      ? 'bg-white border-slate-300 ring-1 ring-pink-200/50 shadow-xs'
                      : 'bg-slate-50/70 border-slate-200 opacity-75'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shrink-0 ${
                          navVisibility.blog
                            ? 'bg-pink-50 text-pink-600 border border-pink-100'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        <Camera className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-sm text-slate-900">Galería de Recuerdos</h4>
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                              navVisibility.blog
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}
                          >
                            {navVisibility.blog ? 'Visible' : 'Oculto'}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-400 block mt-0.5">
                          Rutas: /album y /recuerdos
                        </span>
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <button
                      type="button"
                      onClick={() => handleToggleNavVisibility('blog')}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        navVisibility.blog ? 'bg-pink-500' : 'bg-slate-300'
                      }`}
                      title={navVisibility.blog ? 'Desactivar / Ocultar' : 'Activar / Mostrar'}
                      aria-pressed={navVisibility.blog}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${
                          navVisibility.blog ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                    Muro fotográfico y de videos con descripciones románticas, fechas exactas y ubicaciones automáticas.
                  </p>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                    <span className="text-slate-400 font-medium">Estado en la barra pública:</span>
                    <button
                      type="button"
                      onClick={() => handleToggleNavVisibility('blog')}
                      className={`font-bold transition-colors cursor-pointer ${
                        navVisibility.blog
                          ? 'text-pink-600 hover:text-pink-700'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {navVisibility.blog ? 'Ocultar interfaz' : 'Activar interfaz'}
                    </button>
                  </div>
                </div>

                {/* 3. Cartas Diarias */}
                <div
                  className={`p-5 rounded-2xl border transition-all ${
                    navVisibility.letters
                      ? 'bg-white border-slate-300 ring-1 ring-amber-200/50 shadow-xs'
                      : 'bg-slate-50/70 border-slate-200 opacity-75'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shrink-0 ${
                          navVisibility.letters
                            ? 'bg-amber-50 text-amber-600 border border-amber-100'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-sm text-slate-900">Cartas Diarias</h4>
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                              navVisibility.letters
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}
                          >
                            {navVisibility.letters ? 'Visible' : 'Oculto'}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-400 block mt-0.5">
                          Ruta: /cartas
                        </span>
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <button
                      type="button"
                      onClick={() => handleToggleNavVisibility('letters')}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        navVisibility.letters ? 'bg-amber-500' : 'bg-slate-300'
                      }`}
                      title={navVisibility.letters ? 'Desactivar / Ocultar' : 'Activar / Mostrar'}
                      aria-pressed={navVisibility.letters}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${
                          navVisibility.letters ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                    Sobres con cartas programadas por fecha, sellos de cera y dedicatorias íntimas.
                  </p>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                    <span className="text-slate-400 font-medium">Estado en la barra pública:</span>
                    <button
                      type="button"
                      onClick={() => handleToggleNavVisibility('letters')}
                      className={`font-bold transition-colors cursor-pointer ${
                        navVisibility.letters
                          ? 'text-amber-600 hover:text-amber-700'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {navVisibility.letters ? 'Ocultar interfaz' : 'Activar interfaz'}
                    </button>
                  </div>
                </div>

                {/* 4. Frases */}
                <div
                  className={`p-5 rounded-2xl border transition-all ${
                    navVisibility.animations
                      ? 'bg-white border-slate-300 ring-1 ring-purple-200/50 shadow-xs'
                      : 'bg-slate-50/70 border-slate-200 opacity-75'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shrink-0 ${
                          navVisibility.animations
                            ? 'bg-purple-50 text-purple-600 border border-purple-100'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        <Quote className="w-5 h-5 rotate-180" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-sm text-slate-900">Frases</h4>
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                              navVisibility.animations
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}
                          >
                            {navVisibility.animations ? 'Visible' : 'Oculto'}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-400 block mt-0.5">
                          Ruta: /frases
                        </span>
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <button
                      type="button"
                      onClick={() => handleToggleNavVisibility('animations')}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        navVisibility.animations ? 'bg-purple-500' : 'bg-slate-300'
                      }`}
                      title={navVisibility.animations ? 'Desactivar / Ocultar' : 'Activar / Mostrar'}
                      aria-pressed={navVisibility.animations}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${
                          navVisibility.animations ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                    Sección especial de animaciones románticas para la pareja. Oculta o muestra esta pestaña cuando desees.
                  </p>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                    <span className="text-slate-400 font-medium">Estado en la barra pública:</span>
                    <button
                      type="button"
                      onClick={() => handleToggleNavVisibility('animations')}
                      className={`font-bold transition-colors cursor-pointer ${
                        navVisibility.animations
                          ? 'text-purple-600 hover:text-purple-700'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {navVisibility.animations ? 'Ocultar interfaz' : 'Activar interfaz'}
                    </button>
                  </div>
                </div>

                {/* 5. Mi Web */}
                {!isUserWeb && (
                  <div
                    className={`p-5 rounded-2xl border transition-all ${
                      navVisibility.myWeb
                        ? 'bg-white border-slate-300 ring-1 ring-sky-200/50 shadow-xs'
                        : 'bg-slate-50/70 border-slate-200 opacity-75'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shrink-0 ${
                            navVisibility.myWeb
                              ? 'bg-sky-50 text-sky-600 border border-sky-100'
                              : 'bg-slate-100 text-slate-400'
                          }`}
                        >
                          <Globe className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-sm text-slate-900">Mi Web (Solicitudes)</h4>
                            <span
                              className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                                navVisibility.myWeb
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : 'bg-slate-100 text-slate-500 border border-slate-200'
                              }`}
                            >
                              {navVisibility.myWeb ? 'Visible' : 'Oculto'}
                            </span>
                          </div>
                          <span className="text-[10px] font-mono text-slate-400 block mt-0.5">
                            Ruta: /mi-web
                          </span>
                        </div>
                      </div>

                      {/* Toggle Switch */}
                      <button
                        type="button"
                        onClick={() => handleToggleNavVisibility('myWeb')}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          navVisibility.myWeb ? 'bg-sky-500' : 'bg-slate-300'
                        }`}
                        title={navVisibility.myWeb ? 'Desactivar / Ocultar' : 'Activar / Mostrar'}
                        aria-pressed={navVisibility.myWeb}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${
                            navVisibility.myWeb ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                      Permite a los visitantes ver la opción "Mi web" en la barra de navegación para solicitar una web personalizada similar.
                    </p>

                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                      <span className="text-slate-400 font-medium">Estado en la barra pública:</span>
                      <button
                        type="button"
                        onClick={() => handleToggleNavVisibility('myWeb')}
                        className={`font-bold transition-colors cursor-pointer ${
                          navVisibility.myWeb
                            ? 'text-sky-600 hover:text-sky-700'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {navVisibility.myWeb ? 'Ocultar interfaz' : 'Activar interfaz'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Informative Note About Admin Access */}
              <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs flex items-start gap-3 text-xs text-slate-600">
                <ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="font-bold text-slate-900 block">Acceso Administrativo Siempre Garantizado</span>
                  <p className="text-slate-500 leading-relaxed">
                    Independientemente de las interfaces que decidas ocultar en la barra pública, el Panel de Administración siempre estará protegido y accesible para ti mediante el enlace secreto <span className="font-mono text-rose-600 font-semibold">/admin</span> o a través del botón de acceso directo.
                  </p>
                </div>
              </div>

              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: MÚSICA & PLAYLIST (/admin/musica) */}
          {/* ======================================================== */}
          {activeSection === 'music' && (
            <div className="space-y-6">
              <MusicAdminSection
                settings={settings}
                activeAdminProfile={activeProfile}
                onSaveSettings={onSaveSettings}
              />
              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: SOLICITUDES MI WEB (/admin/mi-web) */}
          {/* ======================================================== */}
          {activeSection === 'myweb' && (
            <div className="space-y-6">
              <MyWebAdminSection
                settings={settings}
                requests={webRequests}
                onSaveSettings={onSaveSettings}
                onUpdateStatus={onUpdateWebRequestStatus || (async () => {})}
                onUpdateRequest={onUpdateWebRequest}
                onDeleteRequest={onDeleteWebRequest || (async () => {})}
                triggerToast={(msg) => triggerToast(msg)}
              />
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: GESTIÓN DE LA APP (/admin/app) */}
          {/* ======================================================== */}
          {activeSection === 'app' && (
            <div className="space-y-6">
              {renderSectionBreadcrumbHeader(
                'App Móvil & Descargas',
                'Instala la web app en tu smartphone o genera enlaces de descarga protegidos con duración personalizada.',
                'app',
                <button
                  type="button"
                  onClick={() => onNavigateToPublic('download-app')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-bold transition-all shadow-xs cursor-pointer"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-rose-500" />
                  <span>Ver Página de Descarga Pública</span>
                </button>
              )}
              <AppAdminSection
                settings={settings}
                activeProfile={activeProfile}
                onSaveSettings={onSaveSettings}
                onNavigateToPublic={onNavigateToPublic}
              />
              {renderSectionBottomReturnBar()}
            </div>
          )}

          {/* ======================================================== */}
          {/* SECTION: AJUSTES WEB & MANTENIMIENTO (/admin/ajustes-web) */}
          {/* ======================================================== */}
          {activeSection === 'web_settings' && (
            <div className="space-y-6">
              {renderSectionBreadcrumbHeader(
                'Ajustes Web',
                'Configuración general del sitio web, enlaces de acceso y control maestro para colocar la web en modo mantenimiento.',
                'web_settings',
                <button
                  type="button"
                  onClick={() => onNavigateToPublic('garden')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-xs font-bold transition-all shadow-xs cursor-pointer"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-amber-600" />
                  <span>Ver Web Pública</span>
                </button>
              )}
              <WebSettingsAdminSection
                settings={settings}
                activeProfile={activeProfile}
                onSaveSettings={onSaveSettings}
                onNavigateToPublic={onNavigateToPublic}
              />
              {renderSectionBottomReturnBar()}
            </div>
          )}
        </main>
      </div>

      {/* ======================================================== */}
      {/* MODAL: EDIT SPECIFIC MEMORY */}
      {/* ======================================================== */}
      {editingMemory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
          <div className="bg-white text-slate-900 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl space-y-4 border border-slate-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="font-bold text-base text-slate-900 flex items-center gap-2 font-serif">
                <Edit3 className="w-4 h-4 text-rose-500" />
                <span>Editar Recuerdo</span>
              </h3>
              <button
                onClick={() => setEditingMemory(null)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg cursor-pointer bg-slate-100 hover:bg-slate-200 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEditedMemory} className="space-y-3.5 text-xs">
              {/* Author Attribution Selector */}
              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Autor de este Recuerdo
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setEditingMemory({
                        ...editingMemory,
                        authorRole: 'novio',
                        authorName: p1Name || settings.partner1Name || 'Novio',
                      })
                    }
                    className={`p-2 rounded-xl border text-center transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                      editingMemory.authorRole === 'novio'
                        ? 'bg-sky-50 border-sky-400 text-sky-800 font-bold shadow-xs'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <span>💙</span>
                    <span className="truncate">{p1Name || settings.partner1Name || 'Novio'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setEditingMemory({
                        ...editingMemory,
                        authorRole: 'novia',
                        authorName: p2Name || settings.partner2Name || 'Novia',
                      })
                    }
                    className={`p-2 rounded-xl border text-center transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                      editingMemory.authorRole === 'novia'
                        ? 'bg-rose-50 border-rose-400 text-rose-800 font-bold shadow-xs'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <span>💖</span>
                    <span className="truncate">{p2Name || settings.partner2Name || 'Novia'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setEditingMemory({
                        ...editingMemory,
                        authorRole: 'ambos',
                        authorName: 'Ambos',
                      })
                    }
                    className={`p-2 rounded-xl border text-center transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                      editingMemory.authorRole === 'ambos' || !editingMemory.authorRole
                        ? 'bg-purple-50 border-purple-400 text-purple-800 font-bold shadow-xs'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <span>💑</span>
                    <span>Ambos</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Lugar del Momento</label>
                <input
                  type="text"
                  required
                  value={editingMemory.place}
                  onChange={(e) => setEditingMemory({ ...editingMemory, place: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400 text-slate-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block font-bold text-slate-700">Fecha</label>
                    <label className="text-[10px] text-rose-600 hover:text-rose-700 cursor-pointer flex items-center gap-1 font-semibold">
                      <Sparkles className="w-2.5 h-2.5" />
                      <span>Detectar de foto</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const detected = await detectBestDateFromPhotoBatch([file]);
                          if (detected) {
                            setEditingMemory((prev) => (prev ? { ...prev, date: detected.date } : null));
                          }
                        }}
                      />
                    </label>
                  </div>
                  <input
                    type="date"
                    required
                    value={editingMemory.date}
                    onChange={(e) => setEditingMemory({ ...editingMemory, date: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400 text-slate-900 font-mono"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Categoría</label>
                  <select
                    value={editingMemory.category}
                    onChange={(e) =>
                      setEditingMemory({
                        ...editingMemory,
                        category: e.target.value as any,
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400 text-slate-900 font-medium"
                  >
                    <option value="aniversario">Aniversario</option>
                    <option value="viaje">Viaje</option>
                    <option value="cita">Cita</option>
                    <option value="cotidiano">Cotidiano</option>
                    <option value="especial">Especial</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Mensaje / Carta de Amor</label>
                <textarea
                  rows={3}
                  required
                  value={editingMemory.message}
                  onChange={(e) => setEditingMemory({ ...editingMemory, message: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400 text-slate-900 font-medium"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block font-bold text-slate-700">URL o Foto / Video</label>
                  <label className="text-[10px] text-rose-600 hover:text-rose-700 cursor-pointer flex items-center gap-1 font-semibold">
                    <Upload className="w-2.5 h-2.5" />
                    <span>Cambiar foto y fecha</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const detected = await detectBestDateFromPhotoBatch([file]);
                          const compressed = await compressImageFile(file);
                          setEditingMemory((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  mediaUrl: compressed,
                                  date: detected ? detected.date : prev.date,
                                }
                              : null
                          );
                        } catch (err) {
                          console.error('Error al cambiar foto:', err);
                        }
                      }}
                    />
                  </label>
                </div>
                <input
                  type="text"
                  required
                  value={editingMemory.mediaUrl}
                  onChange={(e) => setEditingMemory({ ...editingMemory, mediaUrl: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400 font-mono text-[11px] text-slate-900"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditingMemory(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl cursor-pointer transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-xl shadow-xs cursor-pointer transition-all"
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* LIVE PREVIEW MODAL FOR TESTING DAILY LETTERS */}
      {/* ======================================================== */}
      {previewLetter && (
        <DailyLetterModal
          letter={previewLetter}
          settings={settings}
          isAdmin={true}
          onClose={() => setPreviewLetter(null)}
          onToggleUnlock={(id) => handleToggleUnlockLetter(id)}
        />
      )}

      {/* ======================================================== */}
      {/* AVISO DE CONFIRMACIÓN (PUBLICAR / ELIMINAR) */}
      {/* ======================================================== */}
      {confirmDialog && (
        <ConfirmationModal
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          description={confirmDialog.description}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
          type={confirmDialog.type}
          previewImage={confirmDialog.previewImage}
          previewDetails={confirmDialog.previewDetails}
          onConfirm={confirmDialog.onConfirm}
          onClose={() => setConfirmDialog(null)}
        />
      )}



      {/* MODAL AUDITORÍA DE LECTURA */}
      <LetterAuditModal
        letter={selectedAuditLetter}
        settings={settings}
        isOpen={Boolean(selectedAuditLetter)}
        onClose={() => setSelectedAuditLetter(null)}
        onTogglePrivacy={handleToggleLetterPrivacy}
      />
    </div>
  );
};
